/**
 * Arena Store - Zustand store for arena state
 */

import { create } from 'zustand';
import type { BoardState, DrawnOpeningInfo, Move, ClockSyncEvent, GameClockInit, TimeControlKey, ChatMessage, ArenaParticipantStatus, OriginePausaArena } from '@draughts/shared';
import { resolveTimeControl, MAX_OFFERTE_PATTA_PER_PARTITA } from '@draughts/shared';
import { applyMove } from '@draughts/core';
import { api } from '../services/api';
import {
  initSocket,
  isConnected,
  setSocketAuthToken,
  ensureSocketAuth,
  wasEvictedByNewSession,
  joinArenaWs,
  leaveArenaWs,
  pauseArena as pauseArenaWs,
  resumeArena as resumeArenaWs,
  arenaAwayWs,
  arenaBackWs,
  watchArena as watchArenaWs,
  unwatchArena as unwatchArenaWs,
  sendMove,
  resignGame,
  offerDraw as socketOfferDraw,
  acceptDraw as socketAcceptDraw,
  declineDraw as socketDeclineDraw,
  boostArena as boostArenaWs,
  unboostArena as unboostArenaWs,
  ArenaSocketError,
  type DrawnOpeningPayload,
  type PrimaMossaPayload,
} from '../services/socket';
import { arenaErrorCode, arenaErrorKey, arenaErrorKeyFor } from '../utils/arena-error-key';
import { ARENA_ERROR_KEYS } from '../utils/arena-error-key';
import { chiaveRifiutoPatta } from '../utils/chiave-rifiuto-patta';
import { sbloccoPattaDopoMossa } from '../utils/sblocco-patta';
import { arenaScadutaMaAttiva } from '../utils/tournament-helpers';
import { annuncioApertura, aperturaStabile } from '../utils/opening-labels';
import { conScartoServer } from '../utils/scarto-server';
import { paginaNascosta } from '../hooks/usePaginaNascosta';
import { useAuthStore } from './auth-store';
import { useChatStore } from './chat-store';
import { log } from '../utils/logger';
import { PAGINA_CLASSIFICA } from '../constants/arena';
import { ARENA_SCORING } from '../types/arena';
import { trackArenaGameStarted, trackArenaGameCompleted, trackArenaPaused, trackArenaResumed } from '../utils/analytics';
import { playMoveSound, playSound } from '../utils/audio';
import { vibrate } from '../utils/vibration';
import type {
  ArenaListItem,
  ArenaStandingEntry,
  ArenaMyParticipant,
  ArenaMatchData,
  ArenaReturnLegPending,
  ArenaGameResult,
  ArenaStandingsUpdate,
  ArenaMyPositionUpdate,
  ArenaExcludedUpdate,
  ArenaAvversarioEscluso,
  ArenaStartedData,
  ArenaEndedData,
  ArenaGameEntry,
  ArenaGameCompletedPayload,
  ArenaScreenStatus,
} from '../types/arena';

/**
 * Quanto deve passare fra due riarmi. Le riconnessioni arrivano a raffica su
 * una rete ballerina, e `arena:join` è limitato a cinque al minuto: senza freno
 * un socket che va e viene brucerebbe il budget e lascerebbe il giocatore fuori
 * dal pool proprio mentre cerca di rientrarci.
 */
const RIARMO_MIN_MS = 10_000;

/** Azzerato da `reset()`, così i test ripartono puliti. */
let ultimoRiarmo = 0;

/**
 * L'ultimo caricamento di dettaglio che qualcuno ha chiesto.
 *
 * `loadArena` ha due attese davanti — `connect()`, fino a tre secondi per il
 * giro del token, e la GET del dettaglio — e nel frattempo si può essere
 * entrati in un'altra arena. Senza questo segnaposto la risposta vecchia
 * riscriveva la pagina nuova, si metteva a guardare la stanza appena
 * lasciata e ci rimandava perfino un `arena:join`.
 *
 * A dire chi ha vinto è **`numero`**, non `nome`: due caricamenti della
 * *stessa* arena si accavallano per davvero — il richiamo periodico della
 * pagina di dettaglio chiede di nuovo lo stesso id mentre quello del
 * montaggio può essere ancora in volo — e per nome sono indistinguibili,
 * quindi la risposta vecchia tornava a sovrascrivere la fresca. `numero`
 * cresce a ogni chiamata: `mia.numero !== richiestaInCorso.numero` significa
 * "qualcuno è passato dopo di me", e vale sia per un'altra arena sia per un
 * secondo giro sulla stessa. `nome` serve solo a scrivere nei log *quale*
 * arena ha preso il posto — `null` quando si è usciti dalla pagina.
 *
 * Azzerato da `reset()` come `ultimoRiarmo` (con `numero` che avanza, così
 * nessuno degli in volo si crede ancora il vincitore): uscire dalla pagina
 * scarta anche quel che è ancora in volo.
 */
let richiestaInCorso: { nome: string | null; numero: number } = { nome: null, numero: 0 };

/*
 * Il tetto di sicurezza della fase pausa/ripresa.
 *
 * `arena:pause` e `arena:resume` sono emit a fondo perduto: nessun ack
 * garantito. Se il server non risponde -- non siamo piu' partecipanti, oppure
 * il socket e' caduto fra l'emit e la conferma -- senza questo tetto i due
 * bottoni resterebbero spenti per sempre, che e' il guasto opposto a quello
 * che la fase serve a chiudere.
 */
const ATTESA_MASSIMA_PAUSA_MS = 5_000;
let timerFasePausa: ReturnType<typeof setTimeout> | null = null;

/** L'elenco delle iscrizioni con `id` dentro, senza doppioni. */
function conIscrizione(elenco: string[], id: string): string[] {
  return elenco.includes(id) ? elenco : [...elenco, id];
}

/** L'elenco delle iscrizioni senza `id`. */
function senzaIscrizione(elenco: string[], id: string | null): string[] {
  if (!id) return elenco;
  return elenco.filter((x) => x !== id);
}

/*
 * Le due facce della pastiglia «Iscritto» nell'elenco.
 *
 * `ArenaCard` legge `arena.isJoined`, che arriva col payload REST dell'elenco
 * e lo riscriveva solo `loadArenas()`: iscriversi o uscire da una scheda non
 * lo toccava, e la card raccontava l'altra storia finche' l'elenco non si
 * rimontava. L'oggetto si ricrea solo se il valore cambia davvero, per non
 * far ridisegnare tutta la lista a ogni gesto.
 */
function conPastiglia<T extends { id: string; isJoined?: boolean }>(elenco: T[], id: string | null): T[] {
  if (!id) return elenco;
  return elenco.map((a) => (a.id === id && a.isJoined !== true ? { ...a, isJoined: true } : a));
}

function senzaPastiglia<T extends { id: string; isJoined?: boolean }>(elenco: T[], id: string | null): T[] {
  if (!id) return elenco;
  return elenco.map((a) => (a.id === id && a.isJoined !== false ? { ...a, isJoined: false } : a));
}

function fermaTimerFasePausa(): void {
  if (timerFasePausa === null) return;
  clearTimeout(timerFasePausa);
  timerFasePausa = null;
}

/**
 * Il broadcast porta le prime venti righe; chi ne aveva chieste altre col
 * bottone "Carica altri" non deve vederle sparire due secondi dopo.
 *
 * Le righe in arrivo vincono sempre dentro la loro pagina — sono le più
 * fresche — e sotto si tiene quello che c'era. Due casi però non sono una
 * coda da tenere, e in entrambi si sostituisce e basta:
 *
 * - **la pagina in arrivo non è piena**: allora è tutta la classifica che
 *   c'è, nessuno sta oltre, e le righe che teniamo in più sono di gente che
 *   non c'è più (chi si ritira senza aver giocato esce dalla classifica).
 *   Tenerle vorrebbe dire lasciarle a schermo fino al prossimo ricarico;
 * - **non abbiamo chiesto niente in più**: chi non ha premuto "Carica altri"
 *   tiene una pagina sola, e la lista non deve allungarsi da sé ogni volta
 *   che qualcuno esce dalle prime venti.
 *
 * Le posizioni della coda sono vecchie e si rinumerano di fila sotto la
 * pagina: è l'unico modo di non perdere chi scivola dalla ventesima alla
 * ventunesima — la sua riga cadrebbe dentro la pagina, e il numero 21 non lo
 * prenderebbe più nessuno. Restano righe un po' vecchie nei punti, non nei
 * posti: si aggiornano al prossimo "Carica altri".
 */
function fondiClassifica(
  arrivate: ArenaStandingEntry[],
  presenti: ArenaStandingEntry[],
): ArenaStandingEntry[] {
  if (arrivate.length < PAGINA_CLASSIFICA) return arrivate;
  if (presenti.length <= arrivate.length) return arrivate;

  const nellaPagina = new Set(arrivate.map((s) => s.userId));
  const coda = presenti.filter((s) => !nellaPagina.has(s.userId));
  if (coda.length === 0) return arrivate;

  return [
    ...arrivate,
    /* L'oggetto si riscrive solo se il numero cambia davvero: ricrearli tutti
       a ogni broadcast farebbe ridisegnare la coda due volte al secondo. */
    ...coda.map((riga, i) => {
      const posto = arrivate.length + i + 1;
      return riga.rank === posto ? riga : { ...riga, rank: posto };
    }),
  ];
}

/** Clock state for arena games */
export interface ArenaClockState {
  white: number;
  black: number;
  activePlayer: 'white' | 'black' | null;
  lastSyncAt: number | null;
  increment: number;
}

interface ArenaStore {
  // Screen state
  status: ArenaScreenStatus;

  /**
   * Una **chiave i18n**, non una frase: `arena.errors.*`. Le pagine rendono
   * `t(error)`. Prima qui finiva `err.message`, cioè l'inglese che il server
   * scrive per i log — e in un caso perfino il testo dell'errore Postgres,
   * `relation "arenas" does not exist`, in mezzo alla pagina.
   *
   * Riguarda i guai di **caricamento** della schermata: il rettangolo rosso
   * sotto l'intestazione. L'esito dell'iscrizione sta in `joinErrorKey`.
   *
   * I tre punti del percorso di partita — `makeMove`, `offerDraw`,
   * `rollbackOptimisticMove` — ci scrivevano ancora una frase inglese: la
   * `It's not your turn` scritta qui, il `res.error` dell'ack della patta
   * (`game-play.handler.ts` risponde con frasi, non con codici) e il
   * `message` di `MOVE_REJECTED`. `t()` su una non-chiave restituisce la
   * stringa stessa, quindi quell'inglese si vedeva davvero: per un fotogramma
   * al rimontaggio della pagina di dettaglio, che `error` lo legge. Adesso
   * anche loro scrivono chiavi; la frase inglese resta nei log, dove serve a
   * chi indaga.
   */
  error: string | null;

  // Arena list
  arenas: ArenaListItem[];
  isLoading: boolean;

  // Current arena
  currentArena: ArenaListItem | null;
  /**
   * Perche' l'arena aperta non si e' fatta.
   *
   * Arriva dall'annuncio di stanza (`arena:cancelled`) e NON dalla riga:
   * `arenas` non ha una colonna `cancel_reason` come i tornei. Quindi vale
   * solo per chi era a schermo nell'istante in cui e' successo — a pagina
   * ricaricata torna `null`, e la scheda dice la frase generica invece di
   * indovinare fra «l'ha annullata chi l'ha aperta» e «non c'era abbastanza
   * gente». Una frase vaga e' meglio di una frase sbagliata.
   */
  motivoAnnullamento: string | null;
  standings: ArenaStandingEntry[];
  /**
   * La classifica dell'arena in cui si GIOCA, quando non e' quella che si
   * guarda.
   *
   * `standings` qui sopra e' filtrata su `currentArena` (vedi
   * `setStandingsUpdate`), cioe' sull'arena APERTA: in coda in A si puo'
   * aprire B dall'elenco e venire accoppiati in A, e da li' la classifica di A
   * veniva ricevuta e buttata. I dati arrivano gia' — chi gioca resta nella
   * stanza `arena:<id>` (`arena:unwatch` non caccia un iscritto) e il
   * broadcast parte entro due secondi, l'accoppiamento stesso lo segna —
   * quindi mancava solo un posto dove metterli.
   *
   * Un secondo campo e non una chiave sul primo: la pagina del dettaglio deve
   * continuare a vedere la classifica di `currentArena`, ed e' esattamente
   * quello che il filtro di oggi protegge. Lo legge solo
   * `ClassificaArenaInPartita`, e solo quando le due arene divergono.
   */
  standingsPartita: ArenaStandingEntry[];
  /**
   * Gli iscritti: chi c'è adesso. Il server li conta su
   * `status IN ('active','paused')`, che è un **sottoinsieme** del filtro
   * della classifica (gli stessi più i ritirati che hanno giocato), quindi
   * `participantCount ≤ righeInClassifica` — sempre, per costruzione. Vale
   * per i due numeri **presi nello stesso istante**, e le finestre in cui non
   * lo sono sono **due**, non una:
   *
   * 1. **dentro il server**: i due numeri nascono da due query separate e non
   *    da uno snapshot. I posti in cui partono **insieme** sono **tre**, non
   *    due: `Promise.all([getStandings, count])` in `arena.service.ts`, in
   *    sequenza nel controller delle classifiche, e — quello che gira più
   *    spesso di tutti — `broadcastStandings` (`arena.handler.ts`, `count()`
   *    subito dopo `getStandings`), che compone `ArenaStandingsResponse` con
   *    tutti e due e la emette su `arena:standings` a ogni fine partita. È da
   *    lì che i due numeri arrivano al client **in regime**, ed è il posto con
   *    la probabilità di collisione più alta: un'iscrizione arrivata fra l'una
   *    e l'altra fa uscire `participantCount = righeInClassifica + 1` **dentro
   *    una sola risposta**;
   * 2. **dopo il server**: qui in mezzo `joinArena` alza questo di uno prima
   *    del broadcast — non un `+1` del client, ma il `count()` fresco che il
   *    server rimanda alla richiesta di iscrizione — e per un paio di secondi
   *    può passarlo.
   *
   * Oggi nessuna delle due specie fa danno perché **nessun consumatore
   * confronta i due numeri**: `participantCount` non arriva più a `ArenaStandings`, e la
   * posizione finale preferisce `righeInClassifica` quando c'è. È il primo
   * confronto fra i due che dovrà rileggere queste righe.
   *
   * L'invariante è fissato dove nasce (`arena.repository.test.ts`, «gli
   * iscritti sono un sottoinsieme della graduatoria») ed è il motivo per cui
   * governare la fascia «+N altri giocatori» su questo numero non poteva
   * promettere righe inesistenti: poteva solo **nasconderne**.
   */
  participantCount: number;
  /**
   * Quante righe ha la graduatoria in tutto — non quanti iscritti ci sono.
   * `participantCount` conta chi c'è adesso (`active`/`paused` lato server);
   * la classifica tiene dentro anche chi si è ritirato dopo aver giocato. È
   * questo il numero che dice se esiste una pagina dopo quella a schermo:
   * governarlo con gli iscritti, in un'arena da sessanta dove quarantacinque
   * sono usciti, nascondeva quaranta righe senza modo di chiederle.
   *
   * `undefined` finché non ha parlato nessuno: il componente ha un ripiego.
   */
  righeInClassifica: number | undefined;
  isJoined: boolean;
  /**
   * Il NOME dell'arena a cui si è iscritti. `isJoined` dice che un'iscrizione
   * c'è, non a quale arena — e le due cose divergono davvero, perché iscriversi
   * a B non ritira da A (`joinArena`, `arena.service.ts:59-99`, tocca solo
   * l'arena chiesta; l'unico `'withdrawn'` è il gesto «Esci», `:114`) e quando
   * B parte il server mette in `arena:B` il socket di ogni iscritto `active`
   * letto dal database (`arena.handler.ts:1386-1389`), trovandolo col ripiego
   * per solo `userId` (`findArenaSocketByUserId` → `findSocketByUserId`,
   * `:936-952`). Da due stanze arrivano due verdetti, e con `currentArena`
   * nullo un booleano non li distingue: vedi `setArenaEnded`.
   *
   * INVARIANTE: si scrive e si azzera **insieme** a `isJoined`, ed è `null`
   * quando quello è falso. Chi aggiunge un posto che scrive `isJoined` deve
   * passare di qui, o la fine della propria arena tornerà a essere buttata via.
   */
  arenaIscrittaId: string | null;
  /**
   * TUTTE le arene a cui si risulta iscritti, non solo l'ultima.
   *
   * `arenaIscrittaId` e' uno slot singolo, ed e' giusto che lo sia: e' l'arena
   * che i gesti nominano (Esci, Pausa, Riprendi), e di arene IN CORSO se ne
   * puo' avere una sola. Ma iscriversi a piu' arene del CALENDARIO e' permesso
   * di proposito, e con un solo slot tornando sulla prima la banda diceva
   * «Partecipa» mentre la card nell'elenco mostrava il badge «iscritto»: due
   * schermate che si contraddicono, e Pausa/Esci irraggiungibili da li'.
   *
   * Serve SOLO a rispondere «sono iscritto a questa?». Per «dove sono davvero»
   * resta `arenaIscrittaId`.
   */
  arenaIscritteIds: string[];
  /**
   * L'arena in cui si sta GIOCANDO.
   *
   * Sono tre cose diverse e finora ne avevano un nome solo due: l'arena che si
   * GUARDA e' `currentArena` (la scrive `loadArena`), quella a cui si e'
   * ISCRITTI e' `arenaIscrittaId`, e quella in cui si GIOCA e' questa. Le
   * prime due divergono appena si apre un'altra arena dall'elenco — restando
   * in coda, cosa che si puo' fare — e da li' la partita veniva attribuita
   * all'arena guardata: cadenza, badge «Valutata», conto alla rovescia e
   * perfino il proprio rating (che si legge sul bucket variante+cadenza)
   * arrivavano dall'arena sbagliata.
   *
   * La scrivono le due porte d'ingresso a una partita, `setArenaMatch` e
   * `rejoinGame`; la azzera `reset()`. Resta scritta anche dopo la fine della
   * partita, perche' il riquadro dell'esito ne ha ancora bisogno: la
   * sovrascrive l'accoppiamento successivo.
   */
  arenaPartitaId: string | null;
  myParticipant: ArenaMyParticipant | null;
  myRank: number | null;
  /**
   * Chi, adesso, non mi puo' essere proposto come avversario — e fino a
   * quando. Lo manda il server per socket, solo a me e solo quando cambia.
   *
   * Vive qui e non dentro `standings` perche' non e' un dato della classifica:
   * la classifica e' la stessa per tutti, questo elenco e' mio.
   */
  excludedOpponents: ArenaAvversarioEscluso[];

  // Game state (when playing)
  currentGameId: string | null;
  currentRoomId: string | null;
  boardState: BoardState | null;
  /**
   * Quante delle semi-mosse in `boardState.moveHistory` le ha messe il
   * sorteggio d'apertura, non i giocatori.
   *
   * Serve alla finestra del boost, che vale «prima della propria prima mossa»
   * e va quindi contata al netto dell'apertura: senza questo numero, tre
   * semi-mosse messe dal server chiuderebbero la finestra prima che i due
   * tocchino la scacchiera. È il gemello lato client di
   * `GameRoom.getPlayedMoveCount()`, che sul server decide la stessa finestra
   * (oltre a rating e punti).
   *
   * Vale 0 su ogni partita senza apertura sorteggiata: il server manda il
   * campo `opening` solo quando il sorteggio c'è stato — sulle arene con
   * `first_move_lottery` a vero e su una variante che una tabella ce l'ha.
   */
  openingPlyCount: number;
  /**
   * Istante (epoch ms) entro cui va giocata la PRIMA mossa vera. In arena la
   * scadenza NON annulla: fa perdere a tavolino, e per questo il server è più
   * generoso (almeno un minuto).
   *
   * Si arma alla RICEZIONE, non quando la damiera compare: l'arena parcheggia
   * l'avvio dietro l'overlay «partita trovata» (e dietro il podio, in
   * `pendingGameStart`), mentre sul server il tempo scorre già da `startClock()`.
   * Armare al momento della comparsa mostrerebbe più tempo di quello vero.
   */
  primaMossaScadeAt: number | null;
  primaMossaEsito: 'void' | 'forfeit' | null;
  /**
   * L'apertura sorteggiata di questa partita, come il giocatore la deve
   * leggere: il testo pronto e le semi-mosse in notazione. `null` quando non
   * c'è stato sorteggio.
   *
   * È il gemello di `multiplayer-store.drawnOpening`, e serve alla stessa
   * cosa: il cartellino sopra la scacchiera (`DrawnOpeningBanner`). In arena
   * quel cartellino è l'UNICA strada — l'overlay «partita trovata», dove
   * torneo e sfida scrivono l'apertura, qui non compare mai perché
   * `setArenaMatch` auto-conferma. Senza, il giocatore si siede davanti a tre
   * semi-mosse già giocate e nessuno gli dice da dove vengono.
   */
  drawnOpening: DrawnOpeningInfo | null;
  /**
   * La manche di questo accoppiamento: 1 andata, 2 ritorno, e quante ne ha in
   * tutto. `null` quando la partita e' unica — che e' quasi sempre.
   *
   * Vivono qui accanto a `drawnOpening` perche' hanno la sua stessa vita e lo
   * stesso lettore: il cartellino sopra la damiera. Il server manda i due
   * numeri su `arena:match` da quando esiste l'andata e ritorno in arena, ma
   * nessuno li teneva, e chi giocava il ritorno non aveva nulla che glielo
   * dicesse — l'overlay «partita trovata» in arena non compare mai.
   */
  manche: number | null;
  mancheTotali: number | null;
  lastMove: Move | null;
  // Pre-move board snapshot for rolling back an optimistic move the server rejects
  preMoveBoardState: BoardState | null;
  playerColor: 'white' | 'black' | null;
  /* `rating` annullabile per la stessa ragione del torneo: chi non ha
     punteggio non ne ha uno da mostrare, e il ripiego che copriva quel caso al
     rientro era un **1000 inventato** — piu' insidioso di uno 0, perche' non si
     vede che e' falso. */
  opponent: { id: string; username: string; rating: number | null; avatar?: string | null } | null;
  gameResult: 'white' | 'black' | 'draw' | null;
  gameEndReason: string | null;

  // Clock state
  clockState: ArenaClockState | null;
  timeControl: TimeControlKey | null;

  // Arena games (for My Games tab)
  arenaGames: ArenaGameEntry[];
  gamesFilter: 'all' | 'mine';

  /**
   * A che punto è l'iscrizione. Il bottone Partecipa non aveva modo di dire
   * niente: in volo restava premibile (ed è così che si arriva al limite di
   * `arena:join`, 5 al minuto), e il fallimento diventava un rettangolo rosso
   * in mezzo alla pagina con la frase inglese del server. `cooldown` è il caso
   * a parte: dopo un `RATE_LIMITED`, "riprova subito" sarebbe un consiglio
   * sbagliato.
   */
  joinPhase: 'idle' | 'pending' | 'failed' | 'cooldown';
  /**
   * Pausa o ripresa in volo. Vedi `pauseSearch`: finche' vale 'pending' i due
   * bottoni sono spenti, ed e' l'unica cosa che impedisce di alternarli a
   * cinque tocchi al secondo fino a sbattere sul tetto del server.
   */
  pausaFase: 'idle' | 'pending';

  /**
   * Da dove nasce la pausa in corso: serve a scrivere la frase della banda, e
   * a niente altro. `null` quando non si sa (nessuna pausa, o un server che
   * non manda l'origine), e la banda allora dice il generico, che resta vero.
   *
   * Qui c'erano DUE booleani — `pausaAutomatica` (l'app in secondo piano) e
   * `pausaAltraArena` — scritti da due ternari paralleli. Con una terza
   * spiegazione da dare («non eri collegato quando l'arena e' partita») e una
   * SECONDA sorgente da cui possono arrivare (il dettaglio REST, non solo
   * l'ack `arena:paused`) sarebbero diventati sei assegnazioni dello stesso
   * fatto: la forma esatta in cui due verita' dello stesso dato divergono, che
   * in questa pagina e' gia' costata due difetti. Un campo solo non puo'
   * divergere da se stesso, e i predicati si derivano dove servono.
   *
   * Le origini che NON si spiegano restano senza frase propria di proposito:
   * `'bottone'` l'utente l'ha premuto lui, e `'fine-partita'` e' la regola del
   * gioco (si resta fermi finche' non si preme «Continua»): raccontarle come
   * una pausa decisa dal server sarebbe falso.
   *
   * Un'`origine` mancante nell'ack non lo cambia: e' il riannuncio di
   * `arena:back`, che ridice «sei ancora fermo» senza sapere da dove nasca la
   * pausa, e non deve cancellare la spiegazione dell'ultima pausa vera.
   */
  originePausa: OriginePausaArena | null;

  /**
   * Perché l'iscrizione non è riuscita, come chiave i18n. Sta a parte da
   * `error`, che è dei guai di caricamento della pagina: l'esito
   * dell'iscrizione lo racconta la banda in fondo, accanto al bottone che
   * l'ha chiesta. Prima non lo raccontava nessuno — il bottone diventava
   * grigio e taceva.
   */
  joinErrorKey: string | null;

  // Boost state (per-game)
  boostActivated: boolean;
  /**
   * A che punto è la richiesta di boost. Serve perché il bottone non può più
   * limitarsi a sparire in silenzio: `pending` mentre il server non ha
   * risposto, `confirmed` per il momento in cui si dice "fatto", `failed`
   * quando non è riuscito ma il momento c'è ancora, `closed` quando non c'è
   * più niente da offrire (preso, confermato, o il server dice troppo tardi).
   */
  boostPhase: 'idle' | 'pending' | 'confirmed' | 'failed' | 'closed';

  // Draw offer state
  drawOffered: boolean;
  opponentOfferedDraw: boolean;
  /** Transient "the opponent refused your draw" banner (offerer only). */
  drawDeclinedNotice: boolean;
  /** Vedi `multiplayer-store`: il freno alle proposte di patta, copia del server. */
  drawOffersLeft: number;
  drawMustMove: boolean;

  // Grace period (opponent disconnected)
  isOpponentDisconnected: boolean;
  opponentDisconnectedColor: 'white' | 'black' | null;
  graceTimeRemaining: number | null;
  /**
   * L'avversario non si e' mai presentato (torneo): la scheda dice «Non
   * presente» invece di «Disconnesso». Letto solo con `isOpponentDisconnected`.
   */
  opponentMaiArrivato: boolean;
  /**
   * Nessun forfait allo scadere della grazia: se non arriva o non rientra, si
   * vince quando scade il SUO orologio (torneo, prima della sua prima mossa).
   * Letto solo con `isOpponentDisconnected`.
   */
  opponentDecideLOrologio: boolean;

  // Match found overlay
  matchFoundData: ArenaMatchData | null;
  hasAcknowledgedMatch: boolean;

  // Pending match (queued while game end overlay is showing)
  pendingMatch: ArenaMatchData | null;
  pendingGameStart: { state: BoardState; clock: GameClockInit | null; openingPlyCount: number; drawnOpening: DrawnOpeningInfo | null } | null;

  /**
   * Il ritorno sta arrivando: l'accoppiamento appena finito era l'andata.
   *
   * Finche' e' valorizzato, l'overlay di fine partita mostra il conto alla
   * rovescia al posto di «Gioca ancora», e la partita in coda si consuma DA
   * SOLA. Non e' una comodita': lato server la stanza del ritorno nasce allo
   * scadere e l'orologio parte con lei, quindi aspettare un click vuol dire
   * guardare il proprio tempo scendere su una damiera che non si sta vedendo.
   */
  ritornoPending: { startsAt: number; opening?: { displayText?: string; moves?: string[] } } | null;

  // Game end result (for post-game overlay)
  lastGameResult: ArenaGameResult | null;
  prevRank: number | null; // rank before current game, for computing rankDelta

  // Final standings (when finished)
  finalStandings: ArenaStandingEntry[] | null;
  winner: { userId: string; username: string; points: number } | null;

  // Actions
  connect: () => Promise<void>;
  loadArenas: () => Promise<void>;
  loadArena: (id: string) => Promise<void>;
  /** La credenziale serve solo alle arene private: il link, o la password. */
  joinArena: (id: string, credential?: { password?: string; token?: string }) => Promise<void>;
  /** L'arena che la pagina mostra, quando c'e': vedi la nota sull'implementazione. */
  leaveArena: (dallaPagina?: string) => void;
  /** `silenziosa`: la pausa e' automatica, un guasto non va detto a schermo. */
  pauseSearch: (silenziosa?: boolean) => void;
  /**
   * `false` = la richiesta NON e' partita (socket giu'), e chi ha premuto deve
   * saperlo. Serve al riquadro di fine partita, che e' l'unica schermata da
   * cui si preme «Continua» e l'unica che `error` non lo legge: senza un
   * esito di ritorno il bottone si premeva all'infinito senza effetto e senza
   * una parola. `true` in tutti gli altri casi, compresi quelli in cui non
   * c'e' niente da emettere (richiesta gia' in volo, partita gia' in coda,
   * arena finita): sono esiti buoni, non guasti.
   */
  resumeSearch: () => boolean;
  makeMove: (move: Move) => void;
  resign: () => void;
  offerDraw: () => void;
  acceptDraw: () => void;
  declineDraw: () => void;
  confirmMatch: () => void;
  rejoinGame: (data: {
    arenaId: string; roomId: string; state: BoardState;
    color: 'white' | 'black'; clock: ClockSyncEvent | null;
    opponent: { id?: string; username: string; rating?: number | null; avatar?: string | null } | null;
    chat?: ChatMessage[];
    /** La cadenza della partita: `clock` porta i tempi ma non l'incremento. */
    timeControl?: string | null;
    /**
     * Quante semi-mosse in `state.moveHistory` le ha messe il sorteggio
     * d'apertura. Ora il rientro la porta: vedi `rejoinGame`.
     */
    openingPlyCount?: number;
    /** Il boost e' acceso? Lo dice il server, che e' l'unico a saperlo. */
    boosted?: boolean;
    /** Quanto resta alla prima mossa: senza, il conto ripartirebbe pieno. */
    primaMossa?: PrimaMossaPayload;
    /** L'apertura sorteggiata, nella forma di `game:start`; assente senza apertura. */
    opening?: DrawnOpeningPayload;
    /** La manche, quando l'incontro ne ha due: vedi `manche` nello stato. */
    manche?: { leg: number; totalLegs: number };
    /**
     * Le NEGOZIAZIONI pendenti e il freno della patta, come nel gemello online.
     *
     * Questa lista di campi e' esplicita, e questi tre non c'erano: un ricarico
     * in arena perdeva la proposta pendente mentre il server la teneva.
     */
    drawOfferBy?: 'WHITE' | 'BLACK' | null;
    takebackOffer?: { color: 'WHITE' | 'BLACK'; toPly: number } | null;
    offertePatta?: { restanti: number; deviMuovere: boolean };
  }) => void;
  activateBoost: () => Promise<void>;
  deactivateBoost: () => Promise<void>;
  /** Chiude il momento del messaggio: `confirmed` → `closed`, `failed` → `idle`. */
  settleBoostPhase: () => void;
  /** Passato il momento del "riprova" (o l'attesa del limite), il bottone torna Partecipa. */
  settleJoinPhase: () => void;
  reset: () => void;
  /**
   * Il logout non e' un'uscita dalla sezione: e' un cambio di persona.
   * `reset()` da solo lascia in piedi l'elenco delle arene — che uscendo dal
   * dettaglio e' giusto tenere — ma cambiando conto quelle card restavano a
   * schermo coi loro "Iscritto" e la loro posizione addosso a chi entra dopo.
   */
  resetAlLogout: () => void;
  /** Rimette in pari lo stato dopo che il socket è rientrato nella stanza dell'arena. */
  rearmArena: (arenaId: string) => void;
  loadArenaGames: () => Promise<void>;
  /**
   * La chiave i18n del guasto nel caricare le partite, `null` se non c'e'.
   *
   * Il fallimento veniva inghiottito da un `catch` che scriveva solo nel log:
   * `arenaGames` restava vuoto e la scheda diceva «Nessuna partita giocata»,
   * cioe' raccontava un'arena senza partite invece di una richiesta andata
   * male. Sono due cose diverse e vanno dette in modo diverso.
   */
  gamesErrorKey: string | null;
  /** Chiede la pagina successiva della classifica e la accoda. */
  loadMoreStandings: () => Promise<void>;
  isLoadingStandings: boolean;
  setGamesFilter: (filter: 'all' | 'mine') => void;
  addCompletedGame: (game: ArenaGameEntry) => void;

  // Clock actions
  initClock: (clockInit: GameClockInit) => void;
  syncClock: (sync: ClockSyncEvent) => void;
  tickClock: () => void;

  // Internal setters (called by socket events)
  setArenaJoined: (arenaId: string, position: number) => void;
  setArenaLeft: (arenaId?: string | null) => void;
  setArenaMatch: (data: ArenaMatchData) => void;
  /** Il ritorno sta per cominciare: arma il conto alla rovescia dell'overlay. */
  setRitornoPending: (data: ArenaReturnLegPending) => void;
  /**
   * Entra nella partita messa in coda dietro l'overlay di fine partita.
   *
   * Torna `false` se non c'era niente in coda, o se c'era solo l'accoppiamento
   * senza ancora il `game:start`. Due chiamanti: «Gioca ancora»
   * (`resumeSearch`) e il ritorno, che non aspetta nessun click.
   */
  consumaPartitaInCoda: () => boolean;
  setStandingsUpdate: (data: ArenaStandingsUpdate) => void;
  setMyPosition: (data: ArenaMyPositionUpdate) => void;
  setExcludedOpponents: (data: ArenaExcludedUpdate) => void;
  setArenaStarted: (data: ArenaStartedData) => void;
  setArenaEnded: (data: ArenaEndedData) => void;
  /** L'arena annullata all'ora d'inizio per mancanza di iscritti (migr. 104). */
  setArenaCancelled: (arenaId: string, motivo?: string) => void;
  /** Apre la fase 'pending' di pausa/ripresa, col tetto di sicurezza. */
  armaFasePausa: () => void;
  /** Chiude la fase 'pending' di pausa/ripresa. Vedi `pauseSearch`. */
  chiudiFasePausa: () => void;
  setArenaPaused: (arenaId: string, participantStatus?: ArenaParticipantStatus, origine?: OriginePausaArena) => void;
  setArenaResumed: (arenaId: string, participantStatus?: ArenaParticipantStatus) => void;
  setGameStart: (state: BoardState, clock: GameClockInit | null, opening?: DrawnOpeningPayload | null, primaMossa?: PrimaMossaPayload | null) => void;
  setGameUpdate: (state: BoardState, lastMove: Move, clock: ClockSyncEvent | null, roomId?: string) => void;
  rollbackOptimisticMove: (message: string) => void;
  setGameEnd: (result: 'white' | 'black' | 'draw', reason: string) => void;
  /**
   * La partita d'arena non c'e' piu': il rientro non l'ha ritrovata.
   *
   * Il rilascio di sicurezza del multiplayer-store guarda la SUA damiera, che
   * per una partita d'arena e' nulla — la posizione sta qui. Senza questa via
   * nessuno azzerava niente, e chi rientrava restava su una damiera d'arena
   * appesa: nessuna mossa arrivava da nessuna parte, nessuna fine partita,
   * nessuna uscita. Si resta iscritti (in pausa): a togliere dall'arena e'
   * un'altra cosa.
   */
  rilasciaPartita: () => void;
  setError: (error: string | null) => void;
  /**
   * Il server ha detto che non siamo in gara: la pagina torna allo stato «non
   * iscritto», l'unico che sa disegnare da qui.
   */
  setNotInArena: () => void;
  setArenaGameCompleted: (payload: ArenaGameCompletedPayload) => void;
  updateArenaPlayerCount: (arenaId: string, playerCount: number) => void;
}

export const useArenaStore = create<ArenaStore>((set, get) => ({
  // Initial state
  status: 'idle',
  error: null,
  arenas: [],
  isLoading: false,
  currentArena: null,
  motivoAnnullamento: null,
  standings: [],
  standingsPartita: [],
  participantCount: 0,
  righeInClassifica: undefined,
  isJoined: false,
  arenaIscrittaId: null,
  arenaIscritteIds: [],
  arenaPartitaId: null,
  myParticipant: null,
  myRank: null,
  excludedOpponents: [],
  currentGameId: null,
  currentRoomId: null,
  boardState: null,
  openingPlyCount: 0,
  primaMossaScadeAt: null,
  primaMossaEsito: null,
  drawnOpening: null,
  manche: null,
  mancheTotali: null,
  lastMove: null,
  preMoveBoardState: null,
  playerColor: null,
  opponent: null,
  gameResult: null,
  gameEndReason: null,
  clockState: null,
  timeControl: null,
  arenaGames: [],
  gamesErrorKey: null,
  /* La scheda si chiama "Le mie partite" e ora si apre su quelle. "Tutte" è
     l'allargamento, a un tocco di distanza. */
  gamesFilter: 'mine',
  isLoadingStandings: false,
  joinPhase: 'idle',
  pausaFase: 'idle',
  originePausa: null,
  joinErrorKey: null,
  boostActivated: false,
  boostPhase: 'idle',
  drawOffered: false,
  opponentOfferedDraw: false,
  drawDeclinedNotice: false,
  drawOffersLeft: MAX_OFFERTE_PATTA_PER_PARTITA,
  drawMustMove: false,
  isOpponentDisconnected: false,
  opponentDisconnectedColor: null,
  graceTimeRemaining: null,
  opponentMaiArrivato: false,
  opponentDecideLOrologio: false,
  matchFoundData: null,
  hasAcknowledgedMatch: false,
  pendingMatch: null,
  pendingGameStart: null,
  ritornoPending: null,
  lastGameResult: null,
  prevRank: null,
  finalStandings: null,
  winner: null,

  // Connect to socket server for arena
  connect: async () => {
    /*
     * A19. Se il server ci ha buttati fuori perche' lo stesso account ha
     * aperto una sessione altrove, riconnettersi evincerebbe quella nuova —
     * che a sua volta rievince questa: ping-pong. `multiplayer-store.connect()`
     * questa guardia ce l'ha (:482); qui mancava, e su questa pagina il
     * richiamo periodico (ARENA_START_RECHECK_MS -> loadArena -> connect) la
     * faceva scattare da sola a ripetizione: `initSocket` a un socket morto
     * ne sostituisce uno vivo, ignorando sia il fermo sia `reconnection =
     * false`. Il fermo lo abbassa un disconnect esplicito (logout / cambio
     * identita'), che e' l'unico momento in cui riconnettersi ha senso.
     */
    if (wasEvictedByNewSession()) {
      log.warn('Arena', 'connect() saltata: la sessione e stata presa da un altro dispositivo');
      return;
    }

    const authState = useAuthStore.getState();
    const token = authState.accessToken;

    /*
     * Anche senza token in memoria, se lo store dice che un'identita' c'e'.
     * `accessToken` non e' persistito: al boot un utente registrato ha
     * `isAuthenticated: true` e `accessToken: null`, e la guardia `if (token)`
     * lo faceva connettere anonimo. `ensureSocketAuth`/`setSocketAuthToken`
     * sanno gia' gestire il nullo, e l'handshake rinnova per conto suo.
     */
    if (token || authState.isAuthenticated) {
      await ensureSocketAuth(token);
    }

    if (!isConnected() && (token || authState.isAuthenticated)) {
      setSocketAuthToken(token);
    }

    // Always call initSocket to register/update arena-specific callbacks
    // Note: onGameStart/onGameUpdate/onGameEnd/onClockSync are routed
    // from the multiplayer store to avoid callback overwrite issues
    /*
     * Niente `onConnect`/`onDisconnect` qui: il registro dei callback è unito
     * per chiave, questo store si registra a ogni `loadArena` — cioè per ultimo
     * — e due gestori che si limitavano a scrivere una riga di log finivano per
     * cancellare quelli del multiplayer, che sono `checkActiveGame` (l'unico
     * rientro automatico in partita dell'app) e lo stato 'disconnected'.
     * Il rientro nella stanza dell'arena dopo una riconnessione arriva invece
     * da `onArenaRewatch`, che ha una chiave tutta sua.
     */
    initSocket({
      onArenaMatch: (data) => {
        get().setArenaMatch(data);
      },
      onArenaReturnLegPending: (data) => {
        get().setRitornoPending(data);
      },
      onArenaStandings: (data) => {
        get().setStandingsUpdate(data);
      },
      onArenaMyPosition: (data) => {
        get().setMyPosition(data);
      },
      onArenaExcluded: (data) => {
        get().setExcludedOpponents(data);
      },
      onArenaStarted: (data) => {
        get().setArenaStarted(data);
      },
      onArenaEnded: (data) => {
        get().setArenaEnded(data);
      },
      onArenaCancelled: ({ arenaId, motivo, iscritti, minimo }) => {
        /* I due numeri ci sono solo per `no_players`: su un annullamento
           dell'organizzatore non esistono, e scriverli «undefined/undefined»
           nel registro manderebbe a cercare un guasto che non c'e'. */
        const conta = iscritti != null && minimo != null ? ` (${iscritti}/${minimo})` : '';
        log.info('Arena', `arena:cancelled ${arenaId} [${motivo}]${conta}`);
        get().setArenaCancelled(arenaId, motivo);
      },
      onArenaPaused: ({ arenaId, participantStatus, origine }) => {
        get().setArenaPaused(arenaId, participantStatus, origine);
      },
      onArenaResumed: ({ arenaId, participantStatus }) => {
        get().setArenaResumed(arenaId, participantStatus);
      },
      onArenaLeft: ({ arenaId }) => {
        get().setArenaLeft(arenaId);
      },
      onArenaError: (error) => {
        /* La frase inglese resta nei log, dove serve a chi indaga; a schermo
           va la traduzione del codice. */
        log.error('Arena', `arena:error (${error.code})`, error.message);
        get().setError(arenaErrorKey(error.code));
        /*
         * Un rifiuto arrivato mentre pausa o ripresa erano in volo.
         *
         * La fase va chiusa subito, o i due bottoni restano spenti fino al
         * tetto di sicurezza. E l'ottimistico gia' scritto va rifatto sul
         * server invece che tenuto: e' esattamente la divergenza che faceva
         * comparire Riprendi, Pausa ed Esci tutti e tre insieme, in modo
         * stabile, quando il tetto del server scartava l'evento in silenzio.
         * Ora il rifiuto si sente, e la pagina si rilegge.
         */
        const pausaInVolo = get().pausaFase === 'pending';
        if (pausaInVolo) get().chiudiFasePausa();
        /*
         * `NOT_IN_ARENA` non e' un guasto: e' il server che dice che in questa
         * arena non ci siamo. Arriva solo dietro un `arena:resume`
         * (`arena.handler.ts:818`, unico posto che lo manda per evento), e
         * `resumeSearch` mette la pagina in ricerca PRIMA di emettere: senza
         * questa riga il giocatore leggeva la banda rossa e restava a fissare
         * una ricerca che nessuno stava facendo. Gli altri codici che passano
         * di qui — RATE_LIMITED, INVALID_INPUT, NOT_AUTHENTICATED — non dicono
         * niente sull'iscrizione, e buttare fuori chi ha solo premuto troppo
         * in fretta sarebbe un guasto nuovo.
         */
        if (error.code === 'NOT_IN_ARENA') {
          get().setNotInArena();
          return;
        }
        /*
         * `GIA_IN_UN_ARENA` per evento arriva da un solo posto: il rifiuto di
         * «Riprendi» quando in un'ALTRA arena viva si e' gia' in gara
         * (`arena.handler.ts`, la guardia di `handleArenaResume`). Il gemello
         * dell'iscrizione passa invece dalla callback, non di qui.
         *
         * Vale la stessa ragione di `NOT_IN_ARENA`: `resumeSearch` scrive
         * l'ottimistico — pagina in ricerca, partecipante `active` — PRIMA di
         * emettere. Senza rimetterlo a posto il giocatore leggeva la banda
         * rossa e restava a fissare una ricerca che nessuno stava facendo, con
         * Pausa ed Esci accesi, fino al ricarico.
         *
         * Si rimette lo stato vero, che il server conosce e non ha toccato:
         * in quest'arena e' ancora fermo. Niente `loadArena`: la verita' la
         * sappiamo gia', e un giro di rete in piu' e' un'altra occasione di
         * divergere.
         *
         * ⚠️ La stessa guardia di `setArenaPaused`: con la damiera a schermo si
         * aggiorna solo l'eco del database, o l'instradamento di `App.tsx`
         * porterebbe fuori da una partita in corso.
         */
        /*
         * `ARENA_SCADUTA`: il tempo dell'arena è finito mentre si premeva.
         *
         * Ci si arriva solo con l'orologio del dispositivo indietro rispetto a
         * quello del server — altrimenti il corto circuito di `resumeSearch` ha
         * già fermato tutto senza emettere — ed è precisamente la ragione per
         * cui le due guardie convivono.
         *
         * Stesso rimedio di `GIA_IN_UN_ARENA`, guardia della damiera compresa:
         * con una partita a schermo si aggiorna solo l'eco del database, o
         * l'instradamento di `App.tsx` porterebbe fuori da una partita in
         * corso. Qui il rosso ci vuole: un bottone è stato premuto e non ha
         * funzionato, e `setError` qui sopra l'ha già scritto.
         */
        if (error.code === 'ARENA_SCADUTA') {
          const { status, myParticipant } = get();
          const partitaAschermo = status === 'playing' || status === 'matchFound' || status === 'gameEnd';
          set({
            ...(partitaAschermo ? {} : { status: 'lobby' as const }),
            ...(myParticipant ? { myParticipant: { ...myParticipant, status: 'paused' as const } } : {}),
          });
          return;
        }
        if (error.code === 'GIA_IN_UN_ARENA') {
          const { status, myParticipant } = get();
          const partitaAschermo = status === 'playing' || status === 'matchFound' || status === 'gameEnd';
          set({
            ...(partitaAschermo ? {} : { status: 'lobby' as const }),
            ...(myParticipant ? { myParticipant: { ...myParticipant, status: 'paused' as const } } : {}),
          });
          return;
        }
        /*
         * E il ricarico di riparazione SOLO per i codici che possono riguardare
         * pausa o ripresa. `pausaFase` e' un booleano globale, non legato alla
         * richiesta: un errore di boost o un `RATE_LIMITED` di un `arena:leave`
         * arrivati mentre una pausa era in volo facevano scattare un
         * `loadArena` sull'arena ISCRITTA — che riscrive `currentArena`,
         * manda `arena:unwatch` a quella che si sta guardando e annulla un
         * caricamento in volo. Cioe' cambiavano l'arena a schermo per colpa di
         * un errore che non c'entrava niente.
         */
        const RIGUARDA_LA_PAUSA = new Set(['RATE_LIMITED', 'INVALID_INPUT', 'NOT_AUTHENTICATED']);
        if (pausaInVolo && RIGUARDA_LA_PAUSA.has(error.code)) {
          const iscritta = get().arenaIscrittaId;
          if (iscritta && iscritta === get().currentArena?.id) void get().loadArena(iscritta);
        }
      },
      onArenaGameCompleted: (data) => {
        get().setArenaGameCompleted(data);
      },
      onArenaListUpdate: (data) => {
        get().updateArenaPlayerCount(data.arenaId, data.playerCount);
      },
      onArenaCreated: () => {
        get().loadArenas();
      },
      onArenaListUpdated: () => {
        /*
         * Un'arena e' partita o e' finita. Chi sta sulla lista non e' nella
         * stanza `arena:<id>` e `arena:started`/`arena:ended` non li riceve:
         * la lista se la rilegge. Nessuna guardia su `isLoading` — quel campo
         * lo condividono la lista e la pagina di dettaglio, e una lettura gia
         * in volo puo' essere partita prima che l'arena cambiasse stato:
         * scartare l'annuncio lascerebbe la lista ferma, che e' il guasto che
         * questo ascoltatore chiude. Succede due volte al giorno.
         */
        void get().loadArenas();
      },
      onArenaRewatch: ({ arenaId }) => {
        /*
         * Il socket e' nuovo, quindi il server non sa piu' niente della nostra
         * presenza: gliela si ridichiara, e si ridichiara lo stato CORRENTE.
         * Una riconnessione avvenuta mentre l'app e' ancora in secondo piano
         * che dicesse «sono tornato» annullerebbe l'assenza e con essa tutta la
         * correzione; una a pagina visibile che dicesse «non sto guardando»
         * metterebbe in pausa chi sta guardando.
         *
         * Sull'arena ISCRITTA, non su quella guardata: `arenaId` qui e' quella
         * che si stava seguendo, che puo' essere la partita di un altro.
         */
        const iscritta = get().arenaIscrittaId;
        if (iscritta) {
          if (paginaNascosta()) arenaAwayWs(iscritta);
          else arenaBackWs(iscritta);
        }
        get().rearmArena(arenaId);
      },
      // Note: onPlayerDisconnected/onPlayerReconnected are routed centrally
      // from multiplayer-store (same pattern as onGameStart/onGameEnd)
    });
  },

  // Load arena list
  loadArenas: async () => {
    log.info('Arena', 'loadArenas() called');
    set({ isLoading: true, error: null });
    try {
      const { arenas } = await api.getArenas();
      set({ arenas, isLoading: false });
    } catch (err) {
      log.error('Arena', 'loadArenas() failed', err);
      set({
        error: arenaErrorKeyFor(err),
        isLoading: false,
      });
    }
  },

  // Load single arena details
  loadArena: async (id: string) => {
    log.info('Arena', 'loadArena() called', { arenaId: id });
    const mia = { nome: id, numero: richiestaInCorso.numero + 1 };
    richiestaInCorso = mia;
    // Unwatch previous arena if any
    const prev = get().currentArena;
    if (prev && prev.id !== id) {
      unwatchArenaWs(prev.id);
    }

    // Preserve active status - don't overwrite searching/matchFound/playing/gameEnd
    // Also preserve lobby when user is joined (paused state) in the SAME arena
    const activeStatuses = new Set(['searching', 'matchFound', 'playing', 'gameEnd']);
    const currentStatus = get().status;
    const currentIsJoined = get().isJoined;
    const sameArena = prev?.id === id || !prev;
    const preserveStatus = activeStatuses.has(currentStatus) || (currentStatus === 'lobby' && currentIsJoined && sameArena);

    /* Il motivo dell'annullamento e' di UN'arena: caricandone un'altra si
       azzera, o la frase di quella di prima finirebbe sotto questa. */
    set({ isLoading: true, error: null, motivoAnnullamento: null, ...(preserveStatus ? {} : { status: 'lobby' }) });
    try {
      // Ensure socket is connected before loading so we can watch for updates
      await get().connect();

      /*
       * Nel frattempo si può essere entrati in un'altra arena, o essere
       * usciti dalla pagina (`reset()` azzera il segnaposto). `isLoading`
       * resta com'è di proposito: spegnerlo qui farebbe sparire lo scheletro
       * mentre la richiesta buona è ancora in volo — la spegne chi ha vinto,
       * quando arriva.
       */
      if (richiestaInCorso.numero !== mia.numero) {
        log.info('Arena', 'loadArena: superata durante connect()', { arenaId: id, ora: richiestaInCorso.nome });
        return;
      }

      const detail = await api.getArena(id);

      if (richiestaInCorso.numero !== mia.numero) {
        log.info('Arena', 'loadArena: risposta in ritardo, scartata', { arenaId: id, ora: richiestaInCorso.nome });
        return;
      }
      const userId = useAuthStore.getState().user?.id;
      const myEntry = userId
        ? detail.standings.find((s) => s.userId === userId)
        : null;

      // Only update status if we're not in an active game flow or paused lobby (same arena)
      const statusNow = get().status;
      const isJoinedNow = get().isJoined;
      const stillActive = activeStatuses.has(statusNow) || (statusNow === 'lobby' && isJoinedNow && sameArena);

      // Preserve myRank/myParticipant when user is joined but not in standings
      // (e.g. user has 0 points and API only returns top standings)
      const newMyRank = detail.myRank ?? (myEntry ? myEntry.rank : (detail.isJoined ? get().myRank : null));
      const newMyParticipant = detail.myParticipant ?? (detail.isJoined ? get().myParticipant : null);

      /*
       * Chi è iscritto e attivo in un'arena in corso sta cercando un
       * avversario — e infatti qui sotto rientriamo nel pool. Dirlo 'lobby'
       * era una bugia che lasciava la barra di stato senza nessun bottone:
       * non è in pausa (lo stato lo dice il server) e non risulta in ricerca,
       * e nessun ramo copre quella combinazione.
       */
      const rientroNelPool =
        detail.isJoined &&
        detail.arena.status === 'active' &&
        (detail.myParticipant?.status ?? 'active') === 'active';

      /*
       * `stillActive` esiste per non far disfare a una risposta REST in ritardo
       * un join appena fatto via socket. Il suo ramo 'lobby' però copre anche
       * chi è iscritto e fermo, e lì "non toccare niente" diventa sbagliato nel
       * momento in cui il server dice che l'arena nel frattempo è partita: qui
       * sotto rientriamo nel pool, e la banda deve dire "cerco un avversario",
       * non "non è ancora cominciata". Senza questo ramo si restava con l'arena
       * attiva e lo schermo in lobby — la combinazione che non ha nemmeno un
       * bottone.
       *
       * È la strada di chi torna sulla pagina dopo essersi perso
       * `arena:started`: il risveglio a orologio e il riarmo dopo una
       * riconnessione passano entrambi di qui. Chi è in pausa non ci entra,
       * perché `rientroNelPool` vuole il partecipante 'active'.
       */
      const risveglioDallaLobby = stillActive && statusNow === 'lobby' && rientroNelPool;

      /*
       * L'arena e' gia' chiusa e ci hai giocato: la pagina che ti serve e' il
       * podio, non la classifica di sempre. `finalStandings` e `winner`
       * arrivavano solo da `arena:ended`, che e' un evento in diretta e non si
       * ripete: bastava non essere online in quell'istante — o ricaricare la
       * pagina, che riporta alla Home perche' le schermate d'arena non hanno un
       * indirizzo proprio — per non vedere mai il proprio piazzamento.
       *
       * Chi e' ancora dentro una partita non ci passa: l'esito a schermo viene
       * prima. `setArenaEnded` fa lo stesso ma su meno stati — preserva
       * 'playing' e 'gameEnd' e NON 'matchFound' (`:1763`) — perche' li' il
       * verdetto arriva mentre si guarda; qui si arriva a cose fatte, e chi ha
       * un accoppiamento appena trovato deve poterlo giocare.
       *
       * ⚠️ L'ordine conta: questo ramo sta PRIMA di `stillActive`. Chi era in
       * 'searching' nell'istante della chiusura sta dentro `activeStatuses`
       * (`:554`), e col ramo messo dopo `stillActive` vincerebbe senza scrivere
       * niente, lasciandolo a cercare per sempre un avversario che non puo' piu'
       * arrivare.
       *
       * ⚠️ Chi ha giocato e poi si e' RITIRATO resta fuori, e non e' una svista:
       * `isJoined` lato server e' `status active|paused` (`arena.service.ts:217`)
       * mentre la graduatoria tiene dentro anche i ritirati che hanno giocato
       * (`arena.repository.ts:471`), quindi il ritirato ha un piazzamento — puo'
       * perfino aver vinto — e vedra' lo stesso la pagina di dettaglio. Allargare
       * la condizione qui non basterebbe: anche le due transizioni di
       * `App.tsx:969` e `:970` pretendono `arenaJoined`. E' una decisione di
       * prodotto, presa e rimandata: chi la cambiera' tocchi tutt'e due i posti.
       */
      /*
       * Il podio puo' dare un nome all'iscrizione solo se un nome non c'e'
       * gia'. Al rientro dopo un ricarico `arenaIscrittaId` e' nullo ed e'
       * giusto scriverlo — e' il campo su cui `setArenaEnded` riconosce i
       * propri verdetti. Ma con un'iscrizione VIVA a un'altra arena scriverlo
       * era un furto: «Esci», «Pausa», «Riprendi» e la dichiarazione di
       * presenza (`useArenaPresence`) parlano tutti di `arenaIscrittaId`, e
       * finivano sull'arena di ieri mentre quella in corso restava senza
       * governo. Al podio serve `status`, non l'identita' dell'iscrizione.
       */
      const iscrizioneViva = get().arenaIscrittaId;
      const podioPuoNominare = iscrizioneViva == null || iscrizioneViva === id;

      /*
       * Chi e' in coda in un'ALTRA arena e' impegnato quanto chi ha gia' la
       * scacchiera davanti: l'accoppiamento puo' arrivare da un istante
       * all'altro, e mandarlo a guardare un podio significa fargli arrivare la
       * partita addosso mentre legge una classifica di ieri.
       *
       * «In un'altra»: la coda nell'arena che si sta aprendo e' il caso
       * opposto e va lasciato al podio, altrimenti chi stava cercando
       * nell'istante della chiusura resterebbe a cercare per sempre un
       * avversario che non puo' piu' arrivare (la casella che lo dice sta in
       * `arena-store.test.ts`).
       */
      const codaAltrove = statusNow === 'searching' && !podioPuoNominare;
      const inPartitaVera =
        codaAltrove ||
        statusNow === 'matchFound' ||
        statusNow === 'playing' ||
        statusNow === 'gameEnd';
      const arenaConclusa = detail.arena.status === 'finished' && detail.isJoined && !inPartitaVera;

      /* Il vincitore e' il primo che ha giocato almeno una partita: la stessa
         regola di `getWinners` lato server, che filtra `games_played > 0`.
         Prendere `standings[0]` incoronerebbe un iscritto a zero partite. */
      const primoCheHaGiocato = detail.standings.find((s) => s.gamesPlayed > 0);
      const vincitore = primoCheHaGiocato
        ? { userId: primoCheHaGiocato.userId, username: primoCheHaGiocato.username, points: primoCheHaGiocato.points }
        : null;

      /*
       * `finalStandings` non vuol dire "l'arena e' finita": vuol dire "la
       * classifica e' intera", e la pagina dei risultati ci conta — offre
       * "Mostra altri" SOLO quando quel campo e' nullo
       * (`ArenaFinalResultsPage.tsx:333`). Ma questa risposta porta una pagina
       * di venti righe (`RIGHE_IN_CLASSIFICA`), non la graduatoria intera:
       * scriverla la' dentro rimetterebbe in piedi la regressione chiusa con
       * `acb732f7`, cioe' il quarantasettesimo che legge "+40 altri giocatori"
       * senza niente da premere. Quando le righe non ci sono tutte si dichiara
       * il nullo — e la pagina ripiega su `standings`, che sono esattamente le
       * stesse righe, con il bottone. Il nullo si scrive comunque, invece di
       * lasciare quello che c'era: era del verdetto di un'ALTRA arena, e
       * finirebbe a schermo dentro questa.
       */
      const classificaIntera =
        detail.righeInClassifica !== undefined && detail.standings.length >= detail.righeInClassifica;

      set({
        currentArena: detail.arena,
        standings: detail.standings,
        participantCount: detail.participantCount,
        righeInClassifica: detail.righeInClassifica,
        isLoading: false,
        // Don't overwrite join/participant state if user is in an active flow
        // (prevents late API response from undoing a WebSocket join)
        ...(arenaConclusa
          ? {
              /* L'identita' dell'iscrizione si scrive solo se non ne calpesta
                 una viva: vedi `podioPuoNominare`. Il resto del podio —
                 `status`, il verdetto, il proprio piazzamento — si scrive
                 comunque, perche' e' della pagina che si sta guardando. */
              ...(podioPuoNominare
                ? {
                    isJoined: true,
                    arenaIscrittaId: id,
                    arenaIscritteIds: conIscrizione(get().arenaIscritteIds, id),
                  }
                : {}),
              myParticipant: newMyParticipant,
              myRank: newMyRank,
              status: 'finished' as const,
              finalStandings: classificaIntera ? detail.standings : null,
              winner: vincitore,
            }
          : stillActive
            ? (risveglioDallaLobby
                ? { status: 'searching' as const, myParticipant: newMyParticipant, myRank: newMyRank }
                /*
                 * Non piu' `{}`: lo stato del partecipante che arriva dal
                 * server e' l'unica cosa che qui va sempre accolta.
                 *
                 * `stillActive` serve a non far disfare a una risposta REST in
                 * ritardo un join appena fatto via socket -- e per `isJoined`,
                 * `status` e il piazzamento resta giusto non toccare niente. Ma
                 * scartando anche `myParticipant` si buttava via la sola verita'
                 * fresca sulla pausa, e una divergenza fra le due verita' del
                 * client diventava irreparabile: ne' una ricarica ne' una
                 * riconnessione riuscivano piu' a rimetterle in pari, perche'
                 * entrambe passano di qui. Restavano Esci e reiscriversi.
                 *
                 * Si scrive solo cio' che il server ha davvero mandato: il
                 * ripiego di `newMyParticipant` vale `null` quando la risposta
                 * dice "non iscritto", ed e' esattamente il caso da cui
                 * `stillActive` difende.
                 */
                : (detail.myParticipant
                    ? {
                        myParticipant: detail.myParticipant,
                        /*
                         * E se il server dice PAUSA, lo stato si allinea.
                         *
                         * Le due verita' del client stavano su campi diversi —
                         * `isPaused` legge `myParticipant.status`, `isSearching`
                         * legge `status` — e questo ramo aggiornava solo la
                         * prima. Chi tornava in primo piano dopo che il setaccio
                         * l'aveva messo in pausa restava «in ricerca» a schermo
                         * mentre nessuno lo accoppiava piu': la combinazione che
                         * la segnalazione descrive.
                         *
                         * Solo da 'searching': gli stati di partita
                         * ('playing', 'matchFound', 'gameEnd') li governa la
                         * partita, e una pausa scritta a database mentre si
                         * gioca e' normale. E non mentre una pausa/ripresa e'
                         * in volo: li' la verita' fresca e' quella locale, e
                         * disfarla farebbe lampeggiare il bottone.
                         */
                        ...(detail.myParticipant.status === 'paused'
                          && statusNow === 'searching'
                          && get().pausaFase === 'idle'
                          ? { status: 'lobby' as const }
                          : {}),
                      }
                    : {}))
            : {
                isJoined: detail.isJoined,
                arenaIscrittaId: detail.isJoined ? id : null,
                arenaIscritteIds: detail.isJoined
                  ? conIscrizione(get().arenaIscritteIds, id)
                  : senzaIscrizione(get().arenaIscritteIds, id),
                myParticipant: newMyParticipant,
                myRank: newMyRank,
                status: detail.isJoined ? (rientroNelPool ? 'searching' : 'lobby') : 'idle',
              }),
        /*
         * La spiegazione della pausa, per chi non l'ha mai sentita.
         *
         * L'ack `arena:paused` porta l'origine solo a chi era collegato nel
         * momento della pausa. Chi aveva spento il telefono quando l'arena e'
         * partita non lo ha ricevuto, e al rientro non manda niente che lo
         * faccia riannunciare: `rientroNelPool` non rimanda `arena:join`
         * (vuole una riga `active`, e la sua e' `paused`) e una pagina appena
         * montata a scheda visibile non emette `visibilitychange`, quindi
         * nemmeno `arena:back`. Questa risposta e' la sola cosa che chiede, e
         * da qui la banda sa cosa dirgli.
         *
         * Fuori dal ternario perche' non e' roba di un ramo solo: il motivo
         * vale allo stesso modo su un'arena viva e su una appena ricaricata.
         * Si scrive solo se il server dice sia «in pausa» sia da dove: un
         * motivo residuo su una riga `active` spiegherebbe una pausa che non
         * c'e' piu', e un `null` di un server piu' vecchio del campo non deve
         * cancellare la spiegazione che un ack aveva gia' portato.
         */
        ...(detail.myParticipant?.status === 'paused' && detail.myParticipant.pauseOrigin
          ? { originePausa: detail.myParticipant.pauseOrigin }
          : {}),
      });

      // Watch arena room for real-time standings updates
      watchArenaWs(id);

      /*
       * Rientro nel pool dopo una caduta di connessione. Il disconnect mette
       * `is_available = false` lasciando `status = 'active'`: senza un join
       * esplicito si resta iscritti e visibili in classifica, ma nessun
       * accoppiamento considera più il giocatore.
       *
       * Solo per chi è attivo: `atomicJoin` fa
       * `ON CONFLICT DO UPDATE SET status='active', is_available=TRUE`, quindi
       * chiamarlo su chi si è messo in pausa lo rimetterebbe in coda al solo
       * ricaricare la pagina. Da fermi si riparte con Riprendi, che ricuce
       * anche la mappa dei socket per conto suo.
       */
      if (rientroNelPool) {
        joinArenaWs(id).then(() => {
          /*
           * Rientrati nel pool mentre l'app e' ancora in secondo piano: lo si
           * dichiara subito, o si resta accoppiabili senza guardare.
           *
           * ⚠️ Si dichiara DOPO il join invece di NON fare il join. Bloccare il
           * join a pagina nascosta sembra piu' diretto, ma lascerebbe fuori dal
           * pool un giocatore legittimamente attivo — la riconnessione puo'
           * capitare in un istante qualunque, e niente lo rimetterebbe dentro
           * quando torna visibile. Cosi' invece decide il server, che e' il
           * principio di tutto il meccanismo: si rientra in coda per qualche
           * secondo e la pausa arriva al tick successivo.
           */
          if (paginaNascosta()) arenaAwayWs(id);
        }).catch((err) => {
          log.warn('Arena', 'rientro nel pool fallito', { arenaId: id, err });
        });
      }
    } catch (err) {
      log.error('Arena', 'loadArena() failed', { arenaId: id, err });
      /* Vale anche per il fallimento: l'arena che si è lasciata non ha
         diritto di mettere il suo errore sulla pagina di un'altra, né di
         spegnerle lo scheletro. */
      if (richiestaInCorso.numero !== mia.numero) return;
      set({
        error: arenaErrorKeyFor(err),
        isLoading: false,
      });
    }
  },

  // Join arena
  joinArena: async (id: string, credential?: { password?: string; token?: string }) => {
    log.info('Arena', 'joinArena() called', { arenaId: id, conCredenziale: !!credential });
    // Un secondo tocco mentre il primo è in volo è esattamente il modo in cui
    // si incontra il limite di 5 al minuto: il debounce da 200ms della pagina
    // non copre un giro di rete.
    if (get().joinPhase === 'pending') return;

    /*
     * Da un'arena IN CORSO non ci si iscrive a un'altra.
     *
     * Prima non serviva dirlo: chi era iscritto non riusciva nemmeno ad aprire
     * un'altra arena, perche' `App.tsx` lo riportava indietro a quella dello
     * store. Tolto quell'aggancio -- che dirottava una scelta esplicita senza
     * una parola -- la regola va scritta dove si decide, ed e' piu' stretta di
     * cosi': guardare resta libero, iscriversi no.
     *
     * Solo per l'arena IN CORSO, non per una del calendario: chi si e' iscritto
     * a quella delle 20:00 deve poter girare, e magari iscriversi anche a
     * quella delle 18:00.
     *
     * Vale anche lato server, dove la doppia iscrizione lascia la mappa dei
     * socket agganciata alla prima arena mentre il giocatore viene accoppiato
     * nella seconda -- da li' il boost risponde GAME_NOT_FOUND e un «Esci»
     * esce dall'arena sbagliata.
     */
    const { arenaIscrittaId, currentArena, arenas } = get();
    if (arenaIscrittaId && arenaIscrittaId !== id) {
      const quellaIscritta = currentArena?.id === arenaIscrittaId
        ? currentArena
        : arenas.find((a) => a.id === arenaIscrittaId);
      if (quellaIscritta?.status === 'active') {
        log.info('Arena', 'joinArena() rifiutata: gia in un arena in corso', {
          arenaId: id, iscrittaA: arenaIscrittaId,
        });
        set({ joinPhase: 'failed', joinErrorKey: arenaErrorKey('GIA_IN_UN_ARENA') });
        return;
      }
    }

    set({ joinPhase: 'pending' });
    try {
      // Ensure socket is connected with proper auth
      await get().connect();

      // Wait for socket to be connected (max 3 seconds)
      let attempts = 0;
      while (!isConnected() && attempts < 30) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      if (!isConnected()) {
        /* Con un codice, non con una frase: da qui in poi l'errore passa dal
           traduttore, e «Could not connect to server» non e traducibile —
           finiva sotto «qualcosa e andato storto» proprio nell'unico caso in
           cui si sa esattamente cosa e successo. */
        throw new ArenaSocketError('NOT_CONNECTED', 'Could not connect to server');
      }

      // Join via WebSocket for real-time updates
      const joinResult = await joinArenaWs(id, credential);

      const { currentArena } = get();
      const newStatus = currentArena?.status === 'active' ? 'searching' : 'lobby';
      set({
        isJoined: true,
        arenaIscrittaId: id,
        arenaIscritteIds: conIscrizione(get().arenaIscritteIds, id),
        /* Lo specchio di `leaveArena`: la card nell'elenco deve accendere la
           pastiglia «Iscritto» senza aspettare un `loadArenas()`. */
        arenas: conPastiglia(get().arenas, id),
        joinPhase: 'idle',
        joinErrorKey: null,
        error: null,
        myParticipant: {
          points: 0,
          gamesPlayed: 0,
          gamesWon: 0,
          gamesDrawn: 0,
          gamesLost: 0,
          boostGames: 0,
          status: 'active' as const,
        },
        myRank: joinResult.position,
        participantCount: joinResult.playerCount,
        status: newStatus,
      });
      log.info('Arena', 'joinArena() success', { arenaId: id, status: newStatus });
    } catch (err) {
      /*
       * Come per il boost: la frase del server è inglese e scritta per i log.
       * `error` resta ai guai di caricamento della pagina — è per quelli che è
       * nato — e l'esito dell'iscrizione lo dicono il bottone e la banda che
       * gli sta accanto, che finalmente ha qualcosa da dire.
       *
       * Il codice si chiede a `arenaErrorCode` e non a `err.code`: la rete
       * caduta lo porta nel messaggio, e guardare solo `.code` la farebbe
       * passare per un guasto sconosciuto.
       */
      const code = arenaErrorCode(err) ?? 'UNKNOWN';
      log.error('Arena', `joinArena() failed (${code})`, err);
      set({
        joinPhase: code === 'RATE_LIMITED' ? 'cooldown' : 'failed',
        joinErrorKey: arenaErrorKey(code),
      });
    }
  },

  settleJoinPhase: () => {
    if (get().joinPhase !== 'pending') set({ joinPhase: 'idle', joinErrorKey: null });
  },

  armaFasePausa: () => {
    fermaTimerFasePausa();
    set({ pausaFase: 'pending' });
    timerFasePausa = setTimeout(() => {
      timerFasePausa = null;
      if (get().pausaFase === 'pending') {
        log.warn('Arena', 'pausa/ripresa senza conferma entro il tetto: fase riaperta');
        set({ pausaFase: 'idle' });
      }
    }, ATTESA_MASSIMA_PAUSA_MS);
  },

  chiudiFasePausa: () => {
    fermaTimerFasePausa();
    if (get().pausaFase !== 'idle') set({ pausaFase: 'idle' });
  },

  // Leave arena
  leaveArena: (dallaPagina?: string) => {
    log.info('Arena', 'leaveArena() called', { dallaPagina });
    /*
     * L'arena che la PAGINA nomina, se ci si risulta iscritti; sotto, l'arena
     * a cui si e' iscritti, e per ultima quella che si sta guardando.
     *
     * Lo slot `arenaIscrittaId` tiene UNA sola arena, l'ultima a cui ci si e'
     * iscritti, mentre essere iscritti a due arene in calendario nella stessa
     * serata e' la configurazione normale — e il bottone «Esci» la pagina lo
     * offre per l'arena che mostra (`arenaIscritteIds.includes`), non per lo
     * slot. Senza questa riga: iscritto ad A e a B, sulla pagina di A premo
     * «Esci» e vengo ritirato da B. In A restavo iscritto e disponibile, e
     * all'ora giusta l'accoppiamento mi dava una partita in un'arena che
     * credevo di aver lasciato — valida per il rating, persa a tempo.
     *
     * Lato server e' esattamente la scelta che `handleArenaLeave` chiede: l'id
     * del client vince su `socketArenas` «perche' la pagina sa quale arena
     * mostra; la mappa no». Finora il client gli mandava l'equivalente della
     * mappa. Il socket non si sposta: `socketArenas.delete` la' e' gia'
     * condizionato a puntare all'arena che si lascia.
     *
     * Il ripiego resta quello di prima, per i chiamanti che non nominano
     * niente e per chi non risulta iscritto da nessuna parte.
     */
    const nominata = dallaPagina && get().arenaIscritteIds.includes(dallaPagina) ? dallaPagina : null;
    const arenaId = nominata ?? get().arenaIscrittaId ?? get().currentArena?.id;
    /*
     * Col socket giu' non e' partito niente: lasciare lo stato ottimistico
     * direbbe «non iscritto» a chi a database e' ancora dentro e disponibile,
     * e al rientro il riarmo lo rimetterebbe in coda in un'arena che credeva
     * di aver lasciato.
     */
    if (!leaveArenaWs(arenaId ?? undefined)) {
      log.warn('Arena', 'leaveArena: socket giu, uscita non inviata', { arenaId });
      set({ error: arenaErrorKey('NOT_CONNECTED') });
      return;
    }
    useChatStore.getState().clearChatContext();
    const { participantCount, arenas } = get();
    set({
      /* Il gesto e' andato: via la banda rossa.
         Vedi la nota su `error` in cima allo store. */
      error: null,
      isJoined: false,
      arenaIscrittaId: null,
      /*
       * Anche l'elenco, come fa `setArenaStarted`: la pastiglia «Iscritto»
       * sulla card legge `arena.isJoined`, che e' un campo del payload REST e
       * lo riscrive solo `loadArenas()`. Senza questa riga restava accesa
       * dopo l'uscita, finche' l'elenco non si rimontava. Non basta
       * `arenaIscritteIds`: quello si riempie solo aprendo o iscrivendosi a
       * un'arena, mai dall'elenco.
       */
      arenas: senzaPastiglia(arenas, arenaId ?? null),
      /* Si esce da QUELL'arena, non da tutte: le altre iscrizioni restano. */
      arenaIscritteIds: senzaIscrizione(get().arenaIscritteIds, arenaId ?? null),
      joinPhase: 'idle',
      joinErrorKey: null,
      myParticipant: null,
      myRank: null,
      excludedOpponents: [],
      participantCount: Math.max(0, participantCount - 1),
      status: 'idle',
    });
  },

  // Pause searching for games
  /*
   * Un secondo tocco mentre il primo e' in volo non aggiunge niente, e
   * alternare Pausa e Riprendi era il modo piu' rapido per rompere tutto: il
   * debounce della pagina e' per singola azione, quindi i due bottoni non si
   * frenavano a vicenda e si arrivava a cinque richieste al secondo per
   * ciascuno. Sbattuto sul tetto del server, l'ottimistico gia' scritto qui
   * restava e le due verita' del client si dividevano per sempre.
   */
  /*
   * `silenziosa` = la pausa non l'ha chiesta l'utente.
   *
   * `pauseSearch` non e' solo il bottone: la chiamano `useArenaQueueGuard` al
   * cambio schermata e al passaggio in secondo piano, `ArenaGame` allo
   * smontaggio, `App` all'abbandono. Col socket giu' quelle chiamate
   * scrivevano `error: 'arena.errors.offline'`, e la banda rossa restava ad
   * aspettare l'utente sulla pagina dell'arena per un gesto che non ha mai
   * fatto.
   */
  pauseSearch: (silenziosa = false) => {
    log.info('Arena', 'pauseArena() called', { silenziosa });
    if (get().pausaFase === 'pending') return;
    const { pendingMatch, pendingGameStart } = get();
    /*
     * Qui l'arena ISCRITTA, non quella guardata — ed e' la differenza con
     * «Esci», che invece nomina l'arena della pagina. Pausa e Ripresa parlano
     * dell'accoppiamento («non darmi piu' partite»), e quello avviene
     * nell'arena in cui si e' in gara, non in quella che si sta sfogliando.
     */
    const arenaId = get().arenaIscrittaId ?? get().currentArena?.id;

    // If a match was queued (server already created the game), resign it before pausing
    if (pendingMatch) {
      log.warn('Arena', 'pauseSearch: pending match exists, resigning before pausing', { roomId: pendingMatch.roomId });
      resignGame(pendingMatch.roomId);
    }

    /* Non partito, non successo: vedi `leaveArena`. Qui il danno di un
       ottimistico lasciato in piedi e' «in pausa» a schermo con il server che
       continua ad accoppiare. */
    if (!pauseArenaWs(arenaId ?? undefined)) {
      log.warn('Arena', 'pauseSearch: socket giu, pausa non inviata', { arenaId, silenziosa });
      if (!silenziosa) set({ error: arenaErrorKey('NOT_CONNECTED') });
      return;
    }
    // Back to the lobby: no game on screen, so no conversation either.
    useChatStore.getState().clearChatContext();
    const partecipante = get().myParticipant;
    get().armaFasePausa();
    set({
      /* Idem `leaveArena`: la pausa e' partita, quindi il guasto di prima non
         c'e' piu' niente da raccontare. */
      error: null,
      status: 'lobby',
      matchFoundData: null,
      hasAcknowledgedMatch: false,
      pendingMatch: null,
      pendingGameStart: null,
      /* Chi si mette in pausa ha scelto di fermarsi: il ritorno non lo
         riguarda piu', e il conto alla rovescia non deve restare acceso. */
      ritornoPending: null,
      // la pausa si vede subito: senza questo la UI resta cieca fino alla
      // classifica successiva, e "Riprendi" non compare
      /* Questa pausa e' partita da qui, quindi non e' una di quelle che il
         server decide da solo: la banda non deve dire «l'app era in secondo
         piano». L'ack la confermera' con `origine: 'bottone'`. */
      originePausa: null,
      ...(partecipante ? { myParticipant: { ...partecipante, status: 'paused' as const } } : {}),
    });
    if (arenaId) trackArenaPaused(arenaId);
  },

  /* Si riparte da un gesto e basta: «Continua» sul riepilogo di fine partita e
     «Riprendi» sulla pagina dell'arena. */
  consumaPartitaInCoda: (): boolean => {
    const { pendingMatch, pendingGameStart } = get();
    if (!pendingMatch || !pendingGameStart) return false;

    /*
     * Si salta del tutto l'overlay «avversario trovato» e si va dritti alla
     * damiera: la partita e' gia' cominciata lato server. Questo percorso non
     * passa da `setArenaMatch`, quindi il contesto della chat si apre qui.
     */
    useChatStore.getState().setChatContext({
      roomId: pendingMatch.roomId,
      playerColor: pendingMatch.color,
      canChat: true,
      source: 'arena',
    });
    /* Questo percorso salta l'overlay, che era l'unico posto da cui il suono
       partiva: senza questa riga la partita cominciava in silenzio. */
    playSound('match-found', { onceForKey: pendingMatch.roomId });
    set({
      /* Si riparte davvero: la banda rossa di un tentativo fallito prima
         non deve sopravvivere alla partita nuova. */
      error: null,
      status: 'playing',
      pendingMatch: null,
      pendingGameStart: null,
      ritornoPending: null,
      matchFoundData: null,
      hasAcknowledgedMatch: true,
      /* Da quale arena viene: vedi `setArenaMatch`. */
      arenaPartitaId: pendingMatch.arenaId,
      currentGameId: pendingMatch.gameId,
      currentRoomId: pendingMatch.roomId,
      playerColor: pendingMatch.color,
      opponent: {
        id: pendingMatch.opponentId,
        username: pendingMatch.opponentUsername,
        rating: pendingMatch.opponentRating,
        avatar: pendingMatch.opponentAvatar ?? null,
      },
      prevRank: get().myRank,
      boardState: pendingGameStart.state,
      openingPlyCount: pendingGameStart.openingPlyCount,
      drawnOpening: pendingGameStart.drawnOpening,
      preMoveBoardState: null,
      lastMove: null,
      gameResult: null,
      gameEndReason: null,
      lastGameResult: null,
      boostActivated: false,
      boostPhase: 'idle',
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
    });
    if (pendingGameStart.clock) {
      get().initClock(pendingGameStart.clock);
    }
    return true;
  },

  resumeSearch: (): boolean => {
    log.info('Arena', 'resumeArena() called');
    /* Stessa ragione della pausa, e la stessa fase: i due bottoni si frenano
       a vicenda solo se guardano lo stesso campo. */
    /* Il primo tocco e' in volo: non e' un guasto, non si dice niente. */
    if (get().pausaFase === 'pending') return true;
    const { pendingMatch, pendingGameStart } = get();

    // If a match was queued while game end overlay was showing, consume it immediately
    if (pendingMatch) {
      if (pendingGameStart) {
        // Both match and game:start arrived while overlay was showing —
        // skip match-found overlay entirely, go straight to playing.
        get().consumaPartitaInCoda();
      } else {
        // Match queued but game:start not yet — show match-found overlay
        set({ error: null, status: 'searching', pendingMatch: null, pendingGameStart: null });
        get().setArenaMatch(pendingMatch);
      }
      return true;
    }

    /*
     * L'arena ISCRITTA, non quella che si guarda: chi è in coda in A e apre B
     * dall'elenco leggeva lo stato di B e decideva su quello. È la stessa
     * domanda che `joinArena` si fa più sotto, e si risolve allo stesso modo.
     */
    const { arenaIscrittaId: idIscritta, currentArena: arenaGuardata, arenas: elenco } = get();
    const arenaDecisiva = idIscritta
      ? (arenaGuardata?.id === idIscritta ? arenaGuardata : elenco.find((a) => a.id === idIscritta) ?? arenaGuardata)
      : arenaGuardata;

    // Se l'arena è finita mentre si leggeva il risultato, non si torna in coda:
    // non c'è più niente da accoppiare.
    if (arenaDecisiva?.status === 'finished') {
      set({ status: 'finished' });
      return true;
    }

    /*
     * Il tempo è scaduto e si aspettano solo le ultime partite degli altri.
     *
     * L'arena è ancora `'active'`, quindi il ramo di sopra non la prende, ma
     * nessun accoppiamento verrà più fatto: emettere `arena:resume` voleva dire
     * farsi rifiutare dal server (`ARENA_SCADUTA`) o — prima che quella guardia
     * esistesse — restare in una coda ferma fino all'ultima partita dell'arena.
     *
     * `'lobby'` e non `'finished'`: quest'ultimo instrada al PODIO
     * (`App.tsx`), e una classifica finale non c'è ancora. In `'lobby'` si
     * torna alla pagina dell'arena, dove la banda dice che il tempo è finito.
     *
     * Niente `error`: quella chiave esce come rettangolo rosso sul dettaglio, e
     * qui non è andato storto niente — è solo l'arena che è finita. Il rosso
     * resta al rifiuto del server, dove qualcuno ha davvero premuto invano.
     *
     * Risponde `true`: `handlePlayNext` fa `setRipresaFallita(!resumeSearch())`
     * e un `false` scriverebbe «ripresa non riuscita» su un gesto che ha fatto
     * esattamente quello che doveva.
     */
    if (arenaDecisiva && arenaScadutaMaAttiva(arenaDecisiva)) {
      const partecipanteOra = get().myParticipant;
      log.info('Arena', 'resumeSearch: arena scaduta, niente ritorno in coda', { arenaId: arenaDecisiva.id });
      set({
        error: null,
        status: 'lobby',
        ...(partecipanteOra ? { myParticipant: { ...partecipanteOra, status: 'paused' as const } } : {}),
      });
      return true;
    }

    /*
     * Qui l'arena ISCRITTA, non quella guardata — ed e' la differenza con
     * «Esci», che invece nomina l'arena della pagina. Pausa e Ripresa parlano
     * dell'accoppiamento («non darmi piu' partite»), e quello avviene
     * nell'arena in cui si e' in gara, non in quella che si sta sfogliando.
     */
    const arenaId = get().arenaIscrittaId ?? get().currentArena?.id;
    const partecipante = get().myParticipant;
    if (!resumeArenaWs(arenaId ?? undefined)) {
      log.warn('Arena', 'resumeSearch: socket giu, ripresa non inviata', { arenaId });
      set({ error: arenaErrorKey('NOT_CONNECTED') });
      return false;
    }
    /* Solo qui: i due rami sopra tornano senza emettere niente, e una fase
       aperta su un evento mai partito non si chiuderebbe mai. */
    get().armaFasePausa();
    set({
      /* La ripresa e' partita: vedi `pauseSearch`. */
      error: null,
      status: 'searching',
      ...(partecipante ? { myParticipant: { ...partecipante, status: 'active' as const } } : {}),
    });
    if (arenaId) trackArenaResumed(arenaId);
    return true;
  },

  // Make a move in current game
  makeMove: (move: Move) => {
    const { currentRoomId, status, boardState, playerColor } = get();
    if (status !== 'playing' || !currentRoomId || !boardState) {
      return;
    }

    const isOurTurn =
      (playerColor === 'white' && boardState.currentTurn === 'WHITE') ||
      (playerColor === 'black' && boardState.currentTurn === 'BLACK');

    if (!isOurTurn) {
      /* Una CHIAVE, non la frase: vedi il contratto di `error` in cima. */
      set({ error: 'arena.errors.notYourTurn' });
      return;
    }

    // Optimistic update: apply the move locally for instant board + sound feedback
    // instead of waiting a network round-trip for the server's game:update echo.
    // The core engine mirrors the server, so reconciliation in setGameUpdate is
    // seamless; preMoveBoardState lets rollbackOptimisticMove restore the board if
    // the server rejects the move (game:error).
    try {
      const optimistic = applyMove(boardState, move);
      set({ boardState: optimistic, lastMove: move, preMoveBoardState: boardState });
      playMoveSound(move.isPromotion, undefined, move.captures.length > 0);
      vibrate(40);
    } catch (err) {
      log.warn('Arena', 'optimistic applyMove failed; sending without local apply', {
        from: move.from,
        to: move.to,
        err,
      });
    }

    sendMove(currentRoomId, move);
  },

  // Resign from current game
  resign: () => {
    const { currentRoomId, status } = get();
    if (currentRoomId && (status === 'playing' || status === 'matchFound')) {
      resignGame(currentRoomId);
    }
  },

  // Draw offer actions
  offerDraw: () => {
    const { currentRoomId, status, drawOffered, opponentOfferedDraw } = get();
    if (status !== 'playing' || !currentRoomId || drawOffered || opponentOfferedDraw) return;
    /* Il freno (vedi `multiplayer-store.offerDraw`): il bottone e' gia' spento,
       e un giro di rete per farsi dire no e' un giro sprecato. */
    if (get().drawMustMove || get().drawOffersLeft <= 0) return;
    /* Lo stato di prima, per rimetterlo se la proposta non parte: vedi
       `multiplayer-store.offerDraw`. */
    const primaDellOfferta = { drawMustMove: get().drawMustMove, drawOffersLeft: get().drawOffersLeft };
    // A new offer clears the previous refusal (see multiplayer-store.offerDraw).
    set({
      drawOffered: true,
      drawDeclinedNotice: false,
      drawMustMove: true,
      drawOffersLeft: Math.max(0, get().drawOffersLeft - 1),
    });
    // Roll back the optimistic flag if the server rejects the offer (e.g. throttled),
    // otherwise the draw button stays stuck on "Draw sent" forever.
    void socketOfferDraw(currentRoomId).then((res) => {
      /* Il conto del server vince sempre; un campo assente non spegne niente. */
      if (res?.offertePatta) {
        set({ drawOffersLeft: res.offertePatta.restanti, drawMustMove: res.offertePatta.deviMuovere });
      }
      if (res?.error && get().drawOffered) {
        /*
         * L'ack di `game:offer-draw` porta una FRASE inglese, non un codice
         * (`game-play.handler.ts:165-207`: 'Room not found', 'Too many draw
         * offers', ...): quella frase finiva dritta in `error`, che per
         * contratto contiene una chiave. Si prova comunque la mappatura, cosi'
         * il giorno che l'ack avra' un `code` sara' gia' tradotto; finche' non
         * ce l'ha, si dice la cosa vera — la proposta di patta non e' partita.
         */
        const codice = arenaErrorCode(res);
        log.warn('Arena', 'proposta di patta rifiutata', { error: res.error, codice });
        /* Senza il conto del server si torna a com'era: l'offerta non e'
           partita, quindi non ha speso niente e non blocca niente. */
        if (!res.offertePatta) set(primaDellOfferta);
        set({
          drawOffered: false,
          /* Il `code` ora c'e' davvero (`game-play.handler`), ed e' il motivo
             del freno: `chiaveRifiutoPatta` ha la frase, condivisa con le altre
             due schermate. Gli altri codici restano alla mappa dell'arena. */
          error: chiaveRifiutoPatta(
            res,
            ARENA_ERROR_KEYS,
            codice ? arenaErrorKey(codice) : 'arena.errors.drawOfferFailed',
          ),
        });
      }
    });
  },

  acceptDraw: () => {
    const { currentRoomId, status, opponentOfferedDraw } = get();
    if (status !== 'playing' || !currentRoomId || !opponentOfferedDraw) return;
    socketAcceptDraw(currentRoomId);
  },

  declineDraw: () => {
    const { currentRoomId, status, opponentOfferedDraw } = get();
    if (status !== 'playing' || !currentRoomId || !opponentOfferedDraw) return;
    socketDeclineDraw(currentRoomId);
    set({ opponentOfferedDraw: false });
  },

  // Confirm match and transition to playing
  confirmMatch: () => {
    const { matchFoundData } = get();
    if (matchFoundData) {
      set({
        status: 'playing',
        matchFoundData: null,
        hasAcknowledgedMatch: true,
      });
    }
  },

  // Rejoin an active arena game after page refresh
  rejoinGame: (data) => {
    // Load arena details in background
    get().loadArena(data.arenaId);

    /*
     * Un rientro SENZA NOME non cancella l'avversario che la pagina ha già.
     *
     * Vedi la nota gemella in `tournament-store.rientraInPartita`, che e' dove
     * il difetto si e' visto: il nome viene dal POSTO in memoria del server
     * (`room.getOpponent`), e quando quel posto non lo porta il payload arriva
     * senza — e questa riga lo scriveva sopra a un'identita' completa, lasciando
     * «Anonimo» col punteggio a trattino per tutto il resto della partita.
     *
     * Solo a parita' di STANZA: l'avversario di prima non deve sopravvivere a
     * un accoppiamento nuovo.
     */
    const stessaStanza = data.roomId === get().currentRoomId;
    const avversarioDiPrima = stessaStanza ? get().opponent : null;
    if (!data.opponent) {
      log.warn('Arena', 'rientro senza il nome dell\'avversario', {
        arenaId: data.arenaId, roomId: data.roomId, stessaStanza, conservato: !!avversarioDiPrima,
      });
    }

    // Same room as before the refresh, so the context is restored rather than
    // reset — e con esso la conversazione, che il server tiene nella room e
    // rimanda a chi rientra.
    useChatStore.getState().setChatContext({
      roomId: data.roomId,
      playerColor: data.color,
      canChat: true,
      source: 'arena',
    });
    if (data.chat) useChatStore.getState().restoreMessages(data.chat);

    set({
      status: 'playing',
      /*
       * INVARIANTE: nell'arena `gameId` e `roomId` sono lo stesso id. Non e'
       * un campo sbagliato accanto a `currentGameId: data.gameId` di
       * `setArenaMatch` (:1273): il rientro il gameId non ce l'ha, e il
       * roomId lo e'. Il server accoppia con `gameId: roomId`
       * (`arena.handler.ts:1045` e `:1055`) e chiude con
       * `handleArenaGameEnd(roomId, ...)` (`game-end.service.ts:310`),
       * rimettendo quel roomId nella emit di `arena:game-completed`
       * (`arena.handler.ts:1139`). E' su questa coincidenza che regge
       * l'eccezione al filtro di stanza (`payload.game.gameId ===
       * currentGameId`, :1738): scriverci `null` disfa il rientro senza
       * cambiare niente d'altro. Chi separasse i due id deve passare di qui.
       */
      currentGameId: data.roomId,
      currentRoomId: data.roomId,
      boardState: data.state,
      /*
       * L'apertura ora il rientro la porta, e non si indovina piu'.
       *
       * Era fissa a zero perche' `game:rejoined` non la mandava, e il commento
       * di allora diceva che allargarlo voleva dire aggiungere l'apertura al
       * payload: fatto (`connection.handler.ts`, tutt'e due le strade di
       * rientro). Con lo zero, chi ricaricava la pagina prima della propria
       * prima mossa su una partita con apertura vedeva le semi-mosse del
       * sorteggio contate come mosse giocate, e il bottone spariva.
       *
       * Il ripiego a zero resta per i server piu' vecchi del campo: sbaglia
       * dalla parte del «non si puo'», che non regala niente a nessuno.
       */
      openingPlyCount: data.openingPlyCount ?? 0,
      /* Il residuo viene dal server: chi ricarica la pagina non ha visto la
         partita cominciare, quindi non puo' ricavarlo da `Date.now()`. */
      primaMossaScadeAt: data.primaMossa ? Date.now() + data.primaMossa.restaMs : null,
      primaMossaEsito: data.primaMossa?.esito ?? null,
      /* L'apertura sorteggiata, che il rientro ora porta: serve all'intestazione
         «APERTURA N° …» e ai grigi della scheda Mosse, non solo al cartellino.
         Stesso oggetto se e' la stessa apertura della stessa stanza, cosi' un
         rientro a meta' partita non riaccende il cartellino sopra la damiera. */
      drawnOpening: stessaStanza
        ? aperturaStabile(get().drawnOpening, annuncioApertura(data.opening))
        : annuncioApertura(data.opening),
      /* La manche, che il rientro ora porta (`campoManche`). Un payload che non
         la porta non cancella quella che la pagina ha gia', purche' sia la
         stessa stanza: stesso patto dell'apertura qui sopra. */
      manche: data.manche?.leg ?? (stessaStanza ? get().manche : null),
      mancheTotali: data.manche?.totalLegs ?? (stessaStanza ? get().mancheTotali : null),
      preMoveBoardState: null,
      playerColor: data.color,
      lastMove: data.state.moveHistory[data.state.moveHistory.length - 1] || null,
      gameResult: null,
      gameEndReason: null,
      matchFoundData: null,
      hasAcknowledgedMatch: true,
      isJoined: true,
      arenaIscrittaId: data.arenaId,
      arenaIscritteIds: conIscrizione(get().arenaIscritteIds, data.arenaId),
      /* L'altra porta d'ingresso a una partita: anche il rientro deve dire in
         quale arena si sta giocando. Vedi `arenaPartitaId`. */
      arenaPartitaId: data.arenaId,
      opponent: data.opponent ? {
        /* L'identita' vera: `oddsUserId` viaggia nel resoconto del rientro da
           sempre, e la stringa vuota che stava qui rendeva l'avversario non
           apribile dal cartellino. */
        id: data.opponent.id ?? '',
        username: data.opponent.username,
        /* NON `?? 1000`: al rientro il punteggio arriva vero dal server, e
           quando manca davvero non si inventa. */
        rating: data.opponent.rating ?? null,
        avatar: data.opponent.avatar ?? null,
      } : avversarioDiPrima,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
      /*
       * Il boost lo dice il server, non si riparte da spento.
       *
       * Finche' era una scelta irreversibile, azzerarlo qui costava solo un
       * bottone che non ricompariva. Ora che si puo' anche spegnere, un client
       * convinto che sia "off" su un boost che il server ha "on" farebbe
       * RIATTIVARE a chi voleva annullare -- e il server risponderebbe
       * ALREADY_BOOSTED, cioe' il contrario di quel che l'utente ha chiesto.
       */
      boostActivated: data.boosted ?? false,
      boostPhase: 'idle',
      /*
       * Le negoziazioni pendenti e il freno della patta tornano col rientro.
       *
       * Il colore identifica l'offerente: le offerte vivono sul POSTO, non sul
       * socket, che al rientro cambia. Un payload che non li porta non spegne
       * niente — mai chiudere il bottone per un campo assente.
       */
      drawOffered: data.drawOfferBy === (data.color === 'white' ? 'WHITE' : 'BLACK'),
      opponentOfferedDraw: data.drawOfferBy != null
        && data.drawOfferBy !== (data.color === 'white' ? 'WHITE' : 'BLACK'),
      ...(data.offertePatta
        ? { drawOffersLeft: data.offertePatta.restanti, drawMustMove: data.offertePatta.deviMuovere }
        : {}),
    });

    /*
     * L'orologio va rimesso in piedi in due tempi. `clock` porta solo i due
     * tempi residui: la cadenza e l'incremento vivono nella preset, e senza
     * `initClock` restavano `null` e 0 dopo ogni rientro — con le soglie
     * dell'allarme tempo (`getClockLowTimeMs`) tarate sul ripiego.
     *
     * `'none'` passa di qui come tutte le altre: `resolveTimeControl` la
     * riconosce ed e' `initClock` (`:1328-1331`) ad azzerare orologio E
     * cadenza. Saltarla lascerebbe in campo l'orologio della partita
     * precedente su una partita che non ne ha. Fuori resta solo la cadenza che
     * non c'e' (un server che non la manda: si tiene la strada di prima) e
     * quella che `resolveTimeControl` non sa leggere, dove torna `null`: li'
     * l'orologio resta com'e', ma il rientro non cade.
     */
    const cadenza = data.timeControl ?? null;
    const config = cadenza ? resolveTimeControl(cadenza) : null;
    if (config) {
      get().initClock({
        white: data.clock?.white ?? config.initialTime,
        black: data.clock?.black ?? config.initialTime,
        increment: config.increment,
        timeControl: cadenza as TimeControlKey,
      });
    }
    /* Poi i valori veri del server, che `initClock` non conosce (chi muove
       adesso, e i millisecondi esatti). */
    if (data.clock) {
      get().syncClock(data.clock);
    }
  },

  // Il boost: paghi tempo per il doppio dei punti se vinci. Il prezzo lo
  // decide il server (`GameRoom.applyBoost`) — l'incremento e metà del tempo
  // base, su qualunque cadenza — e torna in `newTime`: qui non si ricalcola
  // niente, si scrive quello che il server ha detto.
  activateBoost: async () => {
    log.info('Arena', 'activateBoost() called');
    const { currentGameId, boostActivated, boostPhase, playerColor, clockState } = get();
    if (!currentGameId || boostActivated || boostPhase === 'pending') return;

    set({ boostPhase: 'pending' });

    try {
      const response = await boostArenaWs(currentGameId);
      const updates: Partial<ArenaStore> = { boostActivated: true, boostPhase: 'confirmed' };
      if (response.newTime !== undefined && playerColor && clockState) {
        updates.clockState = {
          ...clockState,
          [playerColor]: response.newTime,
          lastSyncAt: Date.now(),
        };
      }
      set(updates);
      log.info('Arena', 'activateBoost() success', { newTime: response.newTime });
    } catch (err) {
      /*
       * I nove esiti non sono la stessa cosa, e prima finivano tutti nello
       * stesso posto: `error`, una stringa inglese che in partita non guarda
       * nessuno (la leggono solo le pagine lista e dettaglio) e che poi
       * riaffiorava là, fuori tempo massimo. Qui il messaggio del server resta
       * nei log e a decidere è il `code`.
       */
      const code = err instanceof ArenaSocketError ? err.code : 'UNKNOWN';
      log.error('Arena', `activateBoost() failed (${code})`, err);

      if (code === 'ALREADY_BOOSTED') {
        // Il server dice che ce l'hai: il prezzo è già stato scalato, e mentire
        // lasciando il bottone lì sarebbe il modo per farlo pagare due volte.
        set({ boostActivated: true, boostPhase: 'confirmed' });
        return;
      }
      if (code === 'TOO_LATE') {
        // Il momento è passato davvero: il bottone se ne va e Focus torna.
        set({ boostPhase: 'closed' });
        return;
      }
      // Non è riuscito, ma il momento potrebbe esserci ancora: si riprova.
      set({ boostPhase: 'failed' });
    }
  },

  /*
   * Il messaggio dura quanto basta a leggerlo, poi il bottone torna a fare il
   * suo mestiere.
   *
   * Confermato torna 'idle', non piu' 'closed': lo stato acceso/spento ora lo
   * racconta `boostActivated`, e la fase serve solo a far durare il messaggio.
   * Chiudere qui vorrebbe dire togliere il bottone subito dopo averlo acceso,
   * cioe' l'esatto contrario di un interruttore -- il boost si cambia finche'
   * non si muove. 'closed' resta riservato all'unico caso in cui la finestra e'
   * davvero finita: il TOO_LATE del server.
   */
  settleBoostPhase: () => {
    const { boostPhase } = get();
    if (boostPhase === 'confirmed' || boostPhase === 'failed') set({ boostPhase: 'idle' });
  },

  /*
   * Spegnere il boost: il ritorno esatto della penalita', non del tempo.
   *
   * Il server rende quanto aveva TOLTO (`GameRoom.boostPenalita`), non il tempo
   * che c'era prima: fra l'accensione e il ripensamento l'orologio puo' aver
   * corso, e restituire l'assoluto farebbe di due tocchi in fila una pausa
   * gratis. Qui, come per l'accensione, non si ricalcola niente: si scrive il
   * `newTime` che il server ha detto.
   *
   * Le fasi e i codici sono quelli di `activateBoost`, e non per simmetria:
   * senza fase il bottone non sapeva di avere una richiesta in volo e il
   * doppio tocco partiva due volte; senza codici, un rifiuto spariva nei log e
   * il giocatore restava a guardare un interruttore che non rispondeva.
   */
  deactivateBoost: async () => {
    log.info('Arena', 'deactivateBoost() called');
    const { currentGameId, boostActivated, boostPhase, playerColor, clockState } = get();
    if (!currentGameId || !boostActivated || boostPhase === 'pending') return;

    set({ boostPhase: 'pending' });

    try {
      const response = await unboostArenaWs(currentGameId);
      const updates: Partial<ArenaStore> = { boostActivated: false, boostPhase: 'confirmed' };
      if (response.newTime !== undefined && playerColor && clockState) {
        updates.clockState = {
          ...clockState,
          [playerColor]: response.newTime,
          lastSyncAt: Date.now(),
        };
      }
      set(updates);
      log.info('Arena', 'deactivateBoost() success', { newTime: response.newTime });
    } catch (err) {
      const code = err instanceof ArenaSocketError ? err.code : 'UNKNOWN';
      log.error('Arena', `deactivateBoost() failed (${code})`, err);

      if (code === 'NOT_BOOSTED') {
        /* Il server dice che gia' non ce l'hai: e' il riallineamento, non un
           guasto. Chi chiedeva di spegnere ottiene quel che voleva. */
        set({ boostActivated: false, boostPhase: 'idle' });
        return;
      }
      if (code === 'TOO_LATE') {
        /* La finestra e' chiusa davvero: il boost resta come sta -- acceso --
           e il bottone se ne va. E' il solo caso in cui 'closed' e' vero. */
        set({ boostPhase: 'closed' });
        return;
      }
      set({ boostPhase: 'failed' });
    }
  },

  /**
   * Il socket è tornato. La stanza `arena:<id>` l'ha già ripresa
   * services/socket.ts; qui si rimette in pari lo stato, perché mentre si era
   * via l'arena può essere partita (o finita) e nessun evento è arrivato.
   * `loadArena` fa da solo il resto: riscrive l'arena, rimanda `arena:watch` e
   * — solo per chi è iscritto e attivo in un'arena in corso — rientra nel pool
   * con `arena:join`. Chi è in pausa non viene svegliato.
   *
   * Chi ha una partita aperta non passa di qui: al rientro in partita pensa
   * `checkActiveGame` del multiplayer store, e un `loadArena` adesso
   * rimetterebbe in coda un giocatore che sta ancora giocando.
   */
  rearmArena: (arenaId: string) => {
    const { currentArena, status } = get();
    if (!currentArena || currentArena.id !== arenaId) return;
    if (status === 'playing' || status === 'matchFound' || status === 'gameEnd') return;
    const ora = Date.now();
    if (ora - ultimoRiarmo < RIARMO_MIN_MS) return;
    ultimoRiarmo = ora;
    log.info('Arena', 'riarmo dopo riconnessione', { arenaId, status });
    /* `loadArena` scrive già `error` da sé quando va male. */
    void get().loadArena(arenaId);
  },

  // Reset to initial state
  reset: () => {
    log.info('Arena', 'reset() called');
    ultimoRiarmo = 0;
    /*
     * La fase di pausa/ripresa muore con la pagina, e il suo timer con lei.
     *
     * Senza, uscendo dalla sezione con una pausa in volo si entrava
     * nell'arena successiva con `pausaFase: 'pending'`: per un massimo di
     * cinque secondi Pausa e Riprendi erano MUTI (escono subito su 'pending'),
     * senza che niente lo spiegasse. Si auto-guariva col tetto, ma e' una
     * finestra di bottoni morti che non ha ragione di esistere.
     */
    fermaTimerFasePausa();
    richiestaInCorso = { nome: null, numero: richiestaInCorso.numero + 1 };
    const prev = get().currentArena;
    if (prev) {
      unwatchArenaWs(prev.id);
    }
    useChatStore.getState().clearChatContext();

    set({
      status: 'idle',
      pausaFase: 'idle',
      originePausa: null,
      error: null,
      /* Chi esce dalla pagina non sta più caricando niente. Nei rami di
         scarto di `loadArena` lo scheletro resta acceso apposta — lo spegne
         la richiesta che ha vinto, quando arriva. Qui però non c'è nessun
         vincitore: se non lo spegne l'uscita, resta acceso per sempre. */
      isLoading: false,
      currentArena: null,
      standings: [],
      standingsPartita: [],
      participantCount: 0,
      righeInClassifica: undefined,
      isJoined: false,
      arenaIscrittaId: null,
      /* `arenaIscritteIds` NON si azzera: e' dell'utente, non della pagina.
         Lo azzera `resetAlLogout`, che e' un cambio di persona. */
      myParticipant: null,
      myRank: null,
      excludedOpponents: [],
      currentGameId: null,
      currentRoomId: null,
      boardState: null,
      openingPlyCount: 0,
      primaMossaScadeAt: null,
      primaMossaEsito: null,
      drawnOpening: null,
      manche: null,
      mancheTotali: null,
      lastMove: null,
      preMoveBoardState: null,
      playerColor: null,
      opponent: null,
      gameResult: null,
      gameEndReason: null,
      clockState: null,
      timeControl: null,
      arenaGames: [],
      gamesErrorKey: null,
      gamesFilter: 'mine',
      /* Un caricamento di altre righe rimasto acceso lasciava "Mostra altri"
         spento per sempre: `loadMoreStandings` esce subito se lo trova
         acceso, e la risposta che l'avrebbe spento non arriva mai. */
      isLoadingStandings: false,
      joinPhase: 'idle',
      joinErrorKey: null,
      boostActivated: false,
      boostPhase: 'idle',
      /* Le offerte di patta sono della partita di prima: chi esce e rientra
         non deve ritrovarsi il "patta?" dell'avversario di un'altra tavola. */
      drawOffered: false,
      opponentOfferedDraw: false,
      drawDeclinedNotice: false,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
      matchFoundData: null,
      hasAcknowledgedMatch: false,
      pendingMatch: null,
      pendingGameStart: null,
      ritornoPending: null,
      lastGameResult: null,
      prevRank: null,
      arenaPartitaId: null,
      finalStandings: null,
      winner: null,
    });
  },

  /*
   * Il logout e' un cambio di persona, non un'uscita dalla sezione.
   *
   * L'elenco delle arene NON e' fra i campi che `reset()` azzera, e non lo e'
   * per una ragione misurata: uscendo dal podio il figlio si monta prima che
   * l'effetto del genitore archivi il verdetto, quindi la lista se ne andava
   * un istante DOPO che `loadArenas` aveva acceso lo scheletro — e lo
   * scheletro vuole `isLoading`, che `reset()` spegne. Restava il pannello
   * «nessuna arena in programma» per tutta la durata della richiesta, a ogni
   * uscita dal podio. Tenendo le card di prima si vede la lista vecchia finche'
   * non arriva quella fresca, che e' quello che ci si aspetta da un elenco.
   *
   * Al logout invece la lista se ne deve andare davvero: erano le sue card, coi
   * suoi "Iscritto" e la sua posizione, e restavano addosso a chi entra dopo.
   */
  resetAlLogout: () => {
    get().reset();
    /* Le iscrizioni sono della PERSONA: `reset()` le tiene apposta (smontare la
       pagina non disiscrive nessuno), il cambio di conto le porta via. */
    set({ arenas: [], arenaIscritteIds: [] });
  },

  // Load arena games from API
  /*
   * Le righe oltre la ventesima non arrivano dal socket: il broadcast si
   * ferma lì apposta. Questa le chiede in pagine da venti e le accoda a
   * quelle che ci sono, saltando i doppioni — una riga può essere arrivata
   * nel frattempo con un aggiornamento della classifica.
   */
  loadMoreStandings: async () => {
    const { currentArena, standings, isLoadingStandings } = get();
    if (!currentArena || isLoadingStandings) return;
    const arenaId = currentArena.id;
    /* Lo scarto si calcola adesso, sulle righe che ci sono al momento della
       richiesta: è la pagina che stiamo chiedendo. */
    const scarto = standings.length;
    set({ isLoadingStandings: true });
    try {
      const risposta = await api.getArenaStandings(arenaId, scarto, PAGINA_CLASSIFICA);
      /*
       * Dopo l'attesa lo stato non è più quello di prima: il broadcast ha
       * riscritto le righe (ogni due secondi) e si può perfino essere passati
       * a un'altra arena. Riscrivere l'array catturato prima dell'await
       * buttava via i primi e mescolava le seconde.
       */
      const dopo = get();
      if (dopo.currentArena?.id !== arenaId) {
        set({ isLoadingStandings: false });
        return;
      }
      const gia = new Set(dopo.standings.map((s) => s.userId));
      const nuove = risposta.standings.filter((s) => !gia.has(s.userId));
      set({
        standings: [...dopo.standings, ...nuove],
        participantCount: risposta.participantCount,
        /* Insieme alle righe, o al secondo giro la fascia si governerebbe su
           un numero vecchio di una pagina. Ma un server che non lo manda
           (versione vecchia) sta tacendo, non dicendo "zero": scriverci
           `undefined` butterebbe via il numero buono che avevamo gia'.

           Se pero' la pagina e' tornata **vuota**, il silenzio si e' rotto da
           solo: abbiamo chiesto oltre lo scarto e non c'era niente, quindi le
           righe della graduatoria sono quelle che abbiamo. E' l'unico modo di
           spegnere il bottone quando il totale e' un multiplo esatto della
           pagina — li' nessuna pagina torna mai corta, e il ripiego del
           componente («l'ultima e' piena, ce n'e' un'altra») offrirebbe per
           sempre un bottone che non porta niente.

           Il costo, che e' silenzio come tutto il resto del ripiego: se in
           quella stessa finestra di deploy l'arena **cresce**, questo numero
           resta indietro e la fascia tace invece di offrire. Cresce quanto
           si vuole: scritto `righeInClassifica = N`, il componente entra nel
           ramo del numero noto e `N > N` e' falso comunque, quindi non serve
           un altro multiplo esatto — basta un iscritto che gioca. E' l'unico
           punto in cui il comportamento nuovo e' peggiore del vecchio, e la
           scena chiede quattro coincidenze insieme: server vecchio in finestra
           di deploy, totale multiplo esatto della pagina, un click a vuoto
           gia' fatto, crescita dopo quel click. Si sana al **primo rientro
           sulla pagina**, non solo al primo broadcast di un server che il
           numero lo manda: `loadArena` riscrive `righeInClassifica`
           incondizionatamente, e con un server vecchio quel campo torna
           `undefined`. */
        ...(risposta.righeInClassifica != null
          ? { righeInClassifica: risposta.righeInClassifica }
          : risposta.standings.length === 0
            ? { righeInClassifica: dopo.standings.length }
            : {}),
        isLoadingStandings: false,
      });
    } catch (error) {
      log.error('Arena', 'loadMoreStandings failed', error);
      set({ isLoadingStandings: false });
    }
  },

  loadArenaGames: async () => {
    const { currentArena } = get();
    if (!currentArena) return;

    try {
      const { games } = await api.getArenaGames(currentArena.id);
      set({ arenaGames: games, gamesErrorKey: null });
    } catch (err) {
      log.error('Arena', 'Failed to load games', err);
      /* Il guasto si dice, invece di far passare la scheda per vuota. */
      set({ gamesErrorKey: arenaErrorKeyFor(err) });
    }
  },

  // Set games filter
  setGamesFilter: (filter: 'all' | 'mine') => {
    set({ gamesFilter: filter });
  },

  // Add a completed game (from socket event)
  addCompletedGame: (game: ArenaGameEntry) => {
    set((state) => ({
      arenaGames: state.arenaGames.some((g) => g.gameId === game.gameId)
        ? state.arenaGames
        : [game, ...state.arenaGames],
    }));
  },

  // ========== Clock Actions ==========

  // Initialize clock from game:start event
  initClock: (clockInit: GameClockInit) => {
    if (!clockInit || clockInit.timeControl === 'none') {
      set({ clockState: null, timeControl: null });
      return;
    }

    set({
      clockState: {
        white: clockInit.white,
        black: clockInit.black,
        activePlayer: null,
        lastSyncAt: Date.now(),
        increment: clockInit.increment,
      },
      timeControl: clockInit.timeControl,
    });
  },

  // Sync clock from server
  syncClock: (sync: ClockSyncEvent) => {
    set((state) => {
      if (state.status !== 'playing') return state;
      return {
        ...state,
        clockState: {
          white: sync.white,
          black: sync.black,
          activePlayer: sync.activePlayer,
          lastSyncAt: Date.now(),
          increment: state.clockState?.increment ?? 0,
        },
      };
    });
  },

  // Local tick for smooth interpolation
  tickClock: () => {
    const { clockState, status } = get();
    if (!clockState || status !== 'playing' || !clockState.activePlayer || !clockState.lastSyncAt) {
      return;
    }

    const elapsed = Date.now() - clockState.lastSyncAt;
    const activePlayer = clockState.activePlayer;
    const newTime = Math.max(0, clockState[activePlayer] - elapsed);

    set({
      clockState: {
        ...clockState,
        [activePlayer]: newTime,
        lastSyncAt: Date.now(),
      },
    });
  },

  // ========== Internal Setters ==========

  setArenaJoined: (arenaId: string, _position: number) => {
    set({
      isJoined: true,
      arenaIscrittaId: arenaId,
      arenaIscritteIds: conIscrizione(get().arenaIscritteIds, arenaId),
      status: 'lobby',
    });
  },

  /*
   * `arena:left` porta il NOME dell'arena da cui si esce
   * (`arena.handler.ts:582`), e qui lo si buttava: lo stesso gesto che
   * `arenaIscrittaId` ripara un file piu' in la'. Scena: «Esci» da A, poi
   * «Partecipa» a B; la conferma di A arriva dopo il join di B — un giro di
   * rete contro un gesto locale che non aspetta nessuno — e cancellava
   * l'iscrizione a B, da cui il verdetto di B scartato.
   *
   * Senza nome (`arenaId: null`, il socket che non risulta iscritto a niente:
   * `arena.handler.ts:547`) non c'e' niente da confrontare e la pulizia si fa
   * comunque, o l'uscita non si chiuderebbe piu'. E se il nome dell'iscrizione
   * manca mentre `isJoined` e' vero — l'invariante rotta, che non dovrebbe
   * accadere — si pulisce lo stesso: restare iscritti per sempre e' peggio.
   */
  setArenaLeft: (arenaId?: string | null) => {
    const iscritta = get().arenaIscrittaId;
    if (arenaId != null && iscritta != null && arenaId !== iscritta) return;
    const uscita = arenaId ?? iscritta;
    set({
      isJoined: false,
      arenaIscrittaId: null,
      arenaIscritteIds: senzaIscrizione(get().arenaIscritteIds, uscita),
      /* Vedi `leaveArena`: anche se l'uscita e' partita da un'altra scheda, la
         card di questa non deve restare col badge «Iscritto». */
      arenas: senzaPastiglia(get().arenas, uscita),
      myParticipant: null,
      status: 'idle',
    });

    /*
     * Rientro nella stanza, da spettatore.
     *
     * Il server ci ha appena tolti da `arena:<id>` (`socket.leave` in
     * `handleArenaLeave`), e quella stanza e' l'UNICO canale della classifica
     * in diretta: chi esce e resta sulla pagina a guardare non riceveva piu'
     * niente, e la classifica gli si fermava al momento dell'uscita fino al
     * ricarico. `arena:watch` e' la porta per rientrarci senza essere in gara.
     *
     * Va fatto qui e non in `leaveArena`: il listener `arena:leave` lato
     * server e' dispatchato in `void` e fa `await` sul database, quindi un
     * `arena:watch` mandato nello stesso tick dell'uscita verrebbe servito
     * PRIMA del `socket.leave`, che poi lo disfa. La conferma `arena:left` e'
     * l'unico momento in cui il kick e' gia' avvenuto.
     *
     * Senza nome non si rifa niente: li' il server e' uscito prima di toccare
     * la stanza, quindi non c'e' nulla da rimettere a posto.
     */
    if (arenaId != null && get().currentArena?.id === arenaId) {
      watchArenaWs(arenaId);
    }
  },

  /*
   * Lo stesso azzeramento di `leaveArena` — lo stato «non iscritto», che la
   * pagina sa disegnare — meno `participantCount`, e non e' una dimenticanza:
   * `leaveArena` lo scala perche' e' lui che ci toglie dall'arena, mentre qui
   * il server ci sta dicendo che non ci ha mai contati (o che ci ha gia'
   * scalati all'abbandono vero). Scalarlo di nuovo mostrerebbe un iscritto in
   * meno di quanti ce ne sono.
   *
   * Va scritto come STATO e non per campi singoli. Rimettere solo `status` e
   * `myParticipant` — il rimedio che avevo registrato la prima volta — a
   * schermo non fa comparire nessun bottone: "Partecipa" e' gated su
   * `!isJoined`, che resta vero; `isPaused` vuole `myParticipant.status ===
   * 'paused'`; `isSearching` vuole `status === 'searching'`. Zero bottoni: un
   * vicolo cieco peggiore del testo contraddittorio, dove almeno un bottone
   * c'era. E `joinPhase: 'idle'` non e' cosmetico: rimasto 'cooldown' o
   * 'failed', il bottone comparirebbe spento e con scritto "Riprova".
   *
   * `error` non si tocca: la banda che spiega il perche' resta, ed e' l'unica
   * cosa che l'azzeramento da solo non direbbe.
   */
  setNotInArena: () => {
    log.info('Arena', 'setNotInArena(): il server dice che non siamo in gara');
    // La conversazione della partita appena finita non sopravvive all'uscita.
    useChatStore.getState().clearChatContext();
    set({
      isJoined: false,
      arenaIscrittaId: null,
      arenaIscritteIds: senzaIscrizione(get().arenaIscritteIds, get().arenaIscrittaId),
      joinPhase: 'idle',
      joinErrorKey: null,
      myParticipant: null,
      myRank: null,
      status: 'idle',
    });
  },

  rilasciaPartita: () => {
    /*
     * Solo una partita che si sta GIOCANDO si rilascia.
     *
     * `gameEnd` e' la schermata di fine partita d'arena, e per il server quella
     * partita e' gia' chiusa: rilasciarla vorrebbe dire strappare via l'esito
     * appena mostrato. 'matchFound' e' l'accoppiamento appena annunciato, con la
     * damiera ancora in arrivo. Ne' l'uno ne' l'altro sono il difetto da chiudere,
     * che e' la damiera VIVA su cui non si puo' piu' fare niente.
     */
    if (get().status !== 'playing' || !get().boardState || get().gameResult) return;
    log.warn('Arena', 'partita d\'arena rilasciata: il server non la conosce piu\'');
    useChatStore.getState().clearChatContext();
    set({
      status: 'lobby',
      currentGameId: null,
      currentRoomId: null,
      boardState: null,
      preMoveBoardState: null,
      lastMove: null,
      playerColor: null,
      opponent: null,
      gameResult: null,
      gameEndReason: null,
      clockState: null,
      drawnOpening: null,
      manche: null,
      mancheTotali: null,
      openingPlyCount: 0,
      primaMossaScadeAt: null,
      primaMossaEsito: null,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
    });
  },

  setRitornoPending: (data: ArenaReturnLegPending) => {
    const { currentArena, arenaPartitaId } = get();
    /*
     * L'arena della PARTITA, non quella che si sta guardando: si puo' finire
     * l'andata in A mentre si tiene aperta la classifica di B, ed e' la stessa
     * distinzione per cui `arena:match` porta con se' il proprio `arenaId`.
     */
    const miaArena = arenaPartitaId ?? currentArena?.id;
    if (miaArena && data.arenaId !== miaArena) {
      log.warn('Arena', 'return-leg-pending di un altra arena, ignorato', { atteso: miaArena, ricevuto: data.arenaId });
      return;
    }
    log.info('Arena', 'ritorno in arrivo', { startsAt: data.startsAt });
    set({
      ritornoPending: {
        /* Sull'orologio di chi guarda, non su quello del server: vedi
           `conScartoServer`. */
        startsAt: conScartoServer(data.startsAt, data.serverTime),
        ...(data.opening ? { opening: data.opening } : {}),
      },
    });
  },

  setArenaMatch: (data: ArenaMatchData) => {
    const { status } = get();
    /* Un server piu' vecchio del campo non manda il totale: una manche sola. */
    const mancheTotali = data.totalLegs ?? 1;
    // State machine validation: expected states are 'searching' or 'gameEnd' (pending)
    if (status !== 'searching' && status !== 'gameEnd') {
      log.warn('Arena', 'setArenaMatch in unexpected state', { status, data });
    }

    // If game end overlay is showing, queue the match instead of transitioning immediately
    if (status === 'gameEnd') {
      set({ pendingMatch: data });
      return;
    }

    // Every arena game is a brand-new room, so this also wipes the previous
    // game's conversation: in an arena the next opponent is paired seconds
    // after the last one, and the two chats must not run together.
    useChatStore.getState().setChatContext({
      roomId: data.roomId,
      playerColor: data.color,
      canChat: true,
      source: 'arena',
    });

    set({
      status: 'matchFound',
      matchFoundData: data,
      hasAcknowledgedMatch: true,  // Auto-confirm: skip overlay in arena (continuous pairing)
      pendingMatch: null,
      /* Da quale arena viene questa partita: `currentArena` non lo sa dire,
         perche' e' l'arena che si sta guardando. Vedi `arenaPartitaId`. */
      arenaPartitaId: data.arenaId,
      currentGameId: data.gameId,
      currentRoomId: data.roomId,
      playerColor: data.color,
      opponent: {
        id: data.opponentId,
        username: data.opponentUsername,
        rating: data.opponentRating,
        avatar: data.opponentAvatar ?? null,
      },
      // Save current rank before game for rankDelta calculation
      prevRank: get().myRank,
      // Reset state for new game
      boardState: null,
      openingPlyCount: 0,
      primaMossaScadeAt: null,
      primaMossaEsito: null,
      drawnOpening: null,
      /* La manche di questo accoppiamento, dall'annuncio: in arena non c'e'
         nessun overlay che la dica, il cartellino e' l'unica strada. */
      manche: mancheTotali > 1 ? (data.leg ?? 1) : null,
      mancheTotali: mancheTotali > 1 ? mancheTotali : null,
      lastMove: null,
      preMoveBoardState: null,
      gameResult: null,
      gameEndReason: null,
      lastGameResult: null,
      boostActivated: false,
      boostPhase: 'idle',
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
    });
  },

  setStandingsUpdate: (data: ArenaStandingsUpdate) => {
    /*
     * Solo la classifica dell'arena che si sta guardando, come già fa
     * `setMyPosition`. Un socket può stare in più stanze — si guarda un'arena
     * restando iscritti a un'altra, e alla partenza il server rimette gli
     * iscritti nella propria — e senza questo filtro la classifica dell'altra
     * riscriveva la pagina ogni due secondi, posizione e punti compresi.
     */
    const { currentArena, arenaPartitaId } = get();

    /*
     * PRIMA del filtro: la classifica dell'arena in cui si GIOCA si tiene da
     * parte, perche' puo' non essere quella che si guarda — e fin qui veniva
     * ricevuta e buttata via dalla riga qui sotto. Vedi `standingsPartita`.
     *
     * Solo le righe: `participantCount`, `righeInClassifica`, `myRank` e
     * `myParticipant` restano governati dal filtro di sempre. Quelli sono i
     * numeri della PAGINA (gli ultimi due sono una faccenda contesa fra questo
     * posto e `setMyPosition`, e non si decide da qui), questo e' solo un
     * elenco che senza un posto dove stare andava perduto.
     */
    if (arenaPartitaId && data.arenaId === arenaPartitaId) {
      set({ standingsPartita: data.standings });
    }

    if (!currentArena || currentArena.id !== data.arenaId) return;

    const userId = useAuthStore.getState().user?.id;
    const myEntry = userId ? data.standings.find((s) => s.userId === userId) : null;

    let myParticipant: ArenaMyParticipant | null;
    let myRank: number | null;

    if (myEntry) {
      myParticipant = {
        points: myEntry.points,
        gamesPlayed: myEntry.gamesPlayed,
        gamesWon: myEntry.gamesWon,
        gamesDrawn: myEntry.gamesDrawn,
        gamesLost: myEntry.gamesLost,
        boostGames: myEntry.boostGames,
        // lo stato lo dice il server, non lo si scrive a mano: prima era
        // fisso su 'active' e ogni classifica cancellava la pausa
        status: myEntry.isWithdrawn ? 'withdrawn' : myEntry.isPaused ? 'paused' : 'active',
      };
      myRank = myEntry.rank;
    } else if (data.myStats) {
      myParticipant = {
        points: data.myStats.points,
        gamesPlayed: data.myStats.gamesPlayed,
        gamesWon: 0,
        gamesDrawn: 0,
        gamesLost: 0,
        boostGames: 0,
        // qui lo stato non viaggia: si conserva quello che si aveva
        status: get().myParticipant?.status ?? 'active',
      };
      /* `?? null` e non `?? get().myRank` come nel ramo qui sotto, ed e' una
         differenza voluta: qui i propri punti sono ARRIVATI, quindi un posto
         mancante vuol dire che un posto non c'e'; li' non e' arrivato niente, e
         il silenzio non cancella il numero che gia' si conosceva. Nell'arena
         questo ramo non si accende mai — `arena:standings` e' un messaggio di
         stanza e non porta campi personali (vedi `ArenaStandingsUpdate`) — ma
         il tipo ora lo dice, e il codice deve reggerlo. */
      myRank = data.myRank ?? null;
    } else {
      myParticipant = get().myParticipant;
      myRank = data.myRank ?? get().myRank;
    }

    // If we're in gameEnd and have a lastGameResult, update rankDelta
    const { status: currentStatus, lastGameResult, prevRank } = get();
    let updatedResult = lastGameResult;
    if (currentStatus === 'gameEnd' && lastGameResult && myRank != null && prevRank != null) {
      const rankDelta = myRank - prevRank; // negative = moved up (good), positive = moved down
      updatedResult = { ...lastGameResult, newRank: myRank, rankDelta };
    }

    set({
      standings: fondiClassifica(data.standings, get().standings),
      participantCount: data.participantCount,
      /* Come sopra: il silenzio di un server vecchio non cancella il numero
         che gia' conoscevamo. */
      ...(data.righeInClassifica != null
        ? { righeInClassifica: data.righeInClassifica }
        : {}),
      myRank,
      myParticipant,
      ...(updatedResult !== lastGameResult ? { lastGameResult: updatedResult } : {}),
    });
  },

  /*
   * La classifica in arrivo porta venti righe: chi sta più in basso non c'è
   * dentro, e la sua posizione arriva da qui, mirata ai due che hanno appena
   * finito una partita.
   */
  setMyPosition: (data: ArenaMyPositionUpdate) => {
    /*
     * Non solo la pagina aperta: anche l'arena a cui si e' iscritti. La pagina
     * aperta non e' per forza quella in cui si gioca — in coda in A si puo'
     * aprire B dall'elenco — e li' la propria posizione veniva scartata,
     * lasciando a schermo «Serie —», i punti fermi e nessun `rankDelta`. Non
     * c'e' nessun'altra fonte che li rimetta a posto: la classifica in diretta
     * si ferma alla ventesima riga, e sotto c'e' solo questo evento. E' lo
     * stesso ripiego che `setArenaEnded` fa gia' sull'iscrizione — con la
     * differenza che qui non si riscrive nessuna schermata altrui: questi due
     * campi sono i PROPRI numeri, e valgono nell'arena in cui si gioca.
     */
    const { currentArena, arenaIscrittaId, myParticipant } = get();
    const laMiaArena = arenaIscrittaId != null && arenaIscrittaId === data.arenaId;
    if (!laMiaArena && currentArena?.id !== data.arenaId) return;
    set({
      myRank: data.rank,
      myParticipant: myParticipant
        ? {
            ...myParticipant,
            points: data.points,
            gamesPlayed: data.gamesPlayed,
            gamesWon: data.gamesWon,
          }
        : myParticipant,
    });
  },

  /*
   * Chi non mi puo' essere proposto adesso.
   *
   * Stesso filtro per arena di `setMyPosition`, e per la stessa ragione: un
   * socket sta in piu' stanze, e l'elenco di un'arena aperta solo per
   * curiosita' non deve sovrascrivere quello dell'arena in cui si gioca.
   */
  setExcludedOpponents: (data: ArenaExcludedUpdate) => {
    const { currentArena, arenaIscrittaId } = get();
    const laMiaArena = arenaIscrittaId != null && arenaIscrittaId === data.arenaId;
    if (!laMiaArena && currentArena?.id !== data.arenaId) return;
    /*
     * Le scadenze si traducono UNA volta, qui, e da qui in poi sono istanti
     * locali come tutti gli altri. Sotto ci gira un conto alla rovescia al
     * secondo (`SegnoRivincita`): un client con l'ora avanti di due minuti
     * mostrerebbe altrimenti un'attesa gia' finita su un divieto ancora vivo,
     * e il numero direbbe il contrario di quello che l'accoppiatore fa.
     */
    const avversari = (data.avversari ?? []).map((a) => ({
      userId: a.userId,
      fino: conScartoServer(a.fino, data.serverTime),
    }));
    set({ excludedOpponents: avversari });
  },

  /*
   * L'arena è partita. Prima qui si metteva solo il giocatore "in ricerca" e
   * `currentArena.status` restava 'scheduled': la banda continuava a dire "Sei
   * iscritto · L'arena non è ancora cominciata", il conto alla rovescia contava
   * verso un inizio già passato e sotto non compariva il bottone Pausa, che
   * esiste solo ad arena attiva. Lo stato dell'arena si aggiorna qui, come fa
   * `setArenaEnded` per 'finished'.
   *
   * L'id serve davvero: l'evento arriva dalla stanza di una singola arena, e
   * chi ne sta guardando un'altra non deve ritrovarsi in ricerca per una a cui
   * non è nemmeno iscritto.
   */
  setArenaStarted: (data: ArenaStartedData) => {
    const { isJoined, currentArena, arenas, status } = get();
    const eLaMia = currentArena?.id === data.arenaId;
    const partita = <T extends ArenaListItem>(a: T): T => ({
      ...a,
      status: 'active' as const,
      endsAt: data.endsAt ?? a.endsAt,
    });
    set({
      /* Anche l'elenco: l'eroe delle arene dice `arena.list.mineScheduled` —
         la stessa frase — finché non lo sa. */
      arenas: arenas.map((a) => (a.id === data.arenaId ? partita(a) : a)),
      ...(eLaMia && currentArena ? { currentArena: partita(currentArena) } : {}),
      /* Solo chi è fermo in attesa: con una partita aperta (o il suo esito
         ancora a schermo) lo stato di schermo è già più avanti di così. */
      ...(eLaMia && isJoined && (status === 'lobby' || status === 'idle')
        ? { status: 'searching' as const }
        : {}),
    });
  },

  /**
   * L'arena che non e' mai cominciata.
   *
   * Molto piu' semplice di `setArenaEnded`, e non per pigrizia: li' c'e' un
   * verdetto da consegnare — classifica, vincitore, podio — e tutta la cura
   * serve a non attribuirlo all'arena sbagliata. Qui non c'e' nessun
   * risultato: l'arena non e' partita, e l'unica cosa da fare e' smettere di
   * dire che sta per cominciare.
   *
   * La guardia sull'id resta, e per la stessa ragione: e' un annuncio di
   * STANZA, e un socket sta in piu' stanze — si guarda un'arena restando
   * iscritti a un'altra. Senza il confronto, l'annullamento di A spegnerebbe
   * la pagina di B.
   */
  setArenaCancelled: (arenaId: string, motivo?: string) => {
    const { currentArena, arenas, arenaIscrittaId, arenaIscritteIds } = get();
    set({
      /* Il motivo si scrive solo se e' l'arena APERTA a essere annullata: e'
         quella la scheda che ci sceglie la frase, e l'annuncio arriva anche
         dalle stanze delle altre a cui si resta iscritti. Senza questa
         guardia, l'annullamento di A metterebbe il suo motivo sotto la
         pagina di B. */
      ...(currentArena?.id === arenaId ? { motivoAnnullamento: motivo ?? null } : {}),
      /* L'elenco si aggiorna comunque: un'arena annullata non deve restare
         «in arrivo» nella lista, anche se non e' quella che si sta guardando. */
      arenas: arenas.map((a) => (a.id === arenaId ? { ...a, status: 'cancelled' as const } : a)),
      ...(currentArena?.id === arenaId
        ? { currentArena: { ...currentArena, status: 'cancelled' as const } }
        : {}),
      /* E non si e' piu' iscritti a una gara che non ci sara': lasciare
         l'iscrizione vorrebbe dire tenere «Esci» e «Pausa» puntati su una
         riga annullata. L'invariante del campo lo impone — si scrive e si
         azzera INSIEME a `isJoined`. */
      ...(arenaIscrittaId === arenaId ? { arenaIscrittaId: null, isJoined: false } : {}),
      arenaIscritteIds: arenaIscritteIds.filter((id) => id !== arenaId),
    });
  },

  setArenaEnded: (data: ArenaEndedData) => {
    const { status, currentGameId, isJoined, arenaIscrittaId, pendingMatch, currentArena, arenas } = get();

    /* L'elenco si aggiorna comunque: un'arena chiusa non deve restare "in
       corso" nella lista, anche se non è quella che si sta guardando. */
    const elenco = arenas.map((a) => (a.id === data.arenaId ? { ...a, status: 'finished' as const } : a));

    /*
     * A20. `arena:ended` è un broadcast di stanza e un socket sta in più
     * stanze — si guarda un'arena restando iscritti a un'altra. Senza questo
     * confronto la chiusura di A marcava finita B, con la classifica e il
     * vincitore di A, e `App.tsx` portava al podio sbagliato sotto il nome di
     * B. È la stessa guardia che `setStandingsUpdate` ha già.
     *
     * Senza arena aperta però non c'è nessuna schermata altrui da proteggere,
     * e c'è invece un'iscrizione: è il rientro dopo un ricarico, dove
     * `rejoinGame` mette `isJoined: true` e lancia `loadArena` senza
     * aspettarla — e se quella GET fallisce `currentArena` resta nullo per
     * tutta la partita. Scartare lì butterebbe via il verdetto della propria
     * arena, che nella sua stanza ce lo rimette il server al rientro
     * (`handleArenaSocketReconnect`, col commento che nomina proprio
     * `arena:ended`).
     *
     * L'iscrizione da sola però non basta, ed è la correzione di una premessa
     * sbagliata che stava scritta qui: NON è vero che col nullo l'unica stanza
     * in cui il socket si trova è la propria. Il giro di partenza dell'arena
     * mette in `arena:<id>` il socket di **ogni** iscritto `active` letto dal
     * database (`arena.handler.ts:1386-1389`), trovandolo col ripiego per solo
     * `userId` (`findArenaSocketByUserId` → `findSocketByUserId`, `:936-952`):
     * nessuna GET, e nessuno sguardo a che cosa il client stia mostrando. E
     * iscritti a due arene insieme ci si sta senza fare niente di strano,
     * perché entrare in B non ritira da A (`arena.service.ts:59-99`). Da lì
     * arrivano due verdetti, e `isJoined` — un booleano — non porta il nome che
     * serve a distinguerli: quello lo porta `arenaIscrittaId`, come
     * `setArenaGameCompleted` si tiene la sua partita per `currentGameId`.
     *
     * Onestà su cosa cambia OGGI a schermo: **niente**. L'unico consumatore di
     * `finalStandings`/`winner` è `ArenaFinalResultsPage`, che con
     * `currentArena` nullo esce prima di renderizzare
     * (`ArenaFinalResultsPage.tsx:119`, `if (!currentArena) return null`) —
     * quindi in quello stato la pagina era bianca prima e resta bianca dopo.
     * La revisione che ha segnalato questo punto diceva il contrario («prima si
     * vedevano classifica e vincitore»): ha letto `currentArena?.name` (`:48`)
     * e `finalStandings || standings` (`:62`) e non la riga 119. Qui si ripara
     * il contratto dello store, non un pixel: lo stato smette di dire che la
     * propria arena è ancora in corso, e chi domani allentasse la riga 119 non
     * si ritroverebbe **questo** difetto — ma dovrà comunque proteggere i
     * quattro accessi a `currentArena` che vengono dopo (`:121`, `:132`,
     * `:137`, `:138`), che senza la 119 sono un `TypeError`.
     *
     * Il confronto è col nome dell'arena aperta se c'è, altrimenti con quello
     * dell'arena a cui si è iscritti: un `??`, non un «o l'una o l'altra».
     * Accettare su `arenaIscrittaId` anche con `currentArena` pieno
     * rimetterebbe la classifica di A dentro la pagina di B, cioè esattamente
     * il danno per cui la guardia A20 esiste.
        *
     * LIMITE NOTO, dichiarato e non chiuso qui. Col ripiego resta un solo caso
     * in cui un verdetto proprio si perde: la pagina di un'ALTRA arena aperta.
     * Lì la precedenza va a `currentArena` e la fine della propria si scarta.
     * Non serve nessun guasto di rete per arrivarci: iscritti e in ricerca in
     * A (`'searching'` è dentro `activeStatuses`, `:534`), si apre la pagina di
     * B dall'elenco — il ramo `stillActive` di `loadArena` riscrive la pagina e
     * l'iscrizione non la tocca (`:607-621`) — poi A finisce, e `status` resta
     * `'searching'` per un'arena che non esiste più. Il danno è lo stato di
     * schermo, non l'elenco, che si aggiorna anche nel ramo di scarto; e si
     * recupera tornando sulla pagina di A.
     *
     * Chi lo chiuderà NON lo faccia allargando questa guardia: accettare anche
     * sul nome dell'iscrizione con la pagina piena è la mutazione «l'una o
     * l'altra», cioè il difetto A20 in persona, e ha la sua rossa. La via è
     * semmai separare le due famiglie di campi del `set` che segue —
     * `finalStandings`/`winner`/`standings` sono della pagina,
     * `status`/`arenas` dell'iscrizione — e scrivere solo le seconde. È un
     * allargamento del contratto della guardia, e va progettato.
     */
    const arenaDiRiferimento = currentArena?.id ?? arenaIscrittaId;
    if (arenaDiRiferimento !== data.arenaId) {
      log.info('Arena', 'arena:ended di un altra arena: aggiorno solo l elenco', {
        arenaId: data.arenaId,
        currentArenaId: currentArena?.id ?? null,
        arenaIscrittaId,
        isJoined,
      });
      set({ arenas: elenco });
      return;
    }

    /*
     * Come per 'playing', anche 'gameEnd' va lasciato stare: l'overlay di fine
     * partita è renderizzato solo per quello stato, e passando a 'finished'
     * spariva lasciando la damiera congelata senza un bottone. L'arena finita
     * si legge da currentArena.status, che aggiorniamo qui sotto.
     */
    const newStatus = status === 'playing' || status === 'gameEnd' ? status : 'finished';
    if (status === 'playing' || status === 'matchFound' || status === 'gameEnd') {
      log.warn('Arena', 'Arena ended while user in active game flow', { status, gameId: currentGameId, hasPendingMatch: !!pendingMatch });
    }
    if (status === 'searching') {
      log.warn('Arena', 'Arena ended while user was searching for game');
    }
    /*
     * Quante righe ha la graduatoria, e se quelle arrivate sono tutte.
     *
     * La fascia «+N altri giocatori» e il denominatore di «#147 su N» si
     * governano su questo numero: preso dalle righe arrivate ripiegherebbe
     * sugli iscritti — che a cose fatte sono di meno, perché chi è uscito dopo
     * aver giocato resta in graduatoria e non fra gli iscritti — e da quando
     * il server mette un tetto di cento righe alla classifica finale
     * (`RIGHE_IN_CLASSIFICA_FINALE`, `arena.handler.ts`) direbbe pure il
     * falso: cento, con centocinquanta in graduatoria.
     *
     * `finalStandings` non vuol dire «l'arena è finita»: vuol dire «la
     * classifica è intera», e la pagina dei risultati ci conta — offre
     * «Mostra altri» SOLO quando quel campo è nullo
     * (`ArenaFinalResultsPage.tsx`). Con la classifica tagliata il verdetto
     * quindi non si dichiara: la pagina ripiega su `standings`, che sono
     * esattamente le stesse righe, col bottone per chiedere le altre via REST.
     * Dichiararlo intero rimetterebbe in piedi la regressione chiusa con
     * `acb732f7`, cioè il quarantasettesimo che legge «+40 altri giocatori»
     * senza niente da premere. È la stessa regola, riga per riga, che
     * `loadArena` applica al ramo dell'arena conclusa (`:688`).
     *
     * Un server vecchio il numero non lo manda: nella finestra di deploy tace,
     * non dice «zero», e si ripiega sulla lunghezza dell'array — cioè su
     * quello che si faceva prima, verdetto intero compreso.
     */
    const righeTotali = data.righeInClassifica ?? data.standings.length;
    const classificaIntera = data.standings.length >= righeTotali;

    set({
      arenas: elenco,
      status: newStatus,
      finalStandings: classificaIntera ? data.standings : null,
      winner: data.winner,
      standings: data.standings,
      righeInClassifica: righeTotali,
      /* Senza arena aperta non se ne inventa una dal payload: `arena:ended`
         non porta il nome, la variante né l'orario, e uno `{...null}` uscirebbe
         come un `ArenaListItem` fatto di un campo solo. */
      ...(currentArena ? { currentArena: { ...currentArena, status: 'finished' as const } } : {}),
    });
  },

  /*
   * La conferma nomina l'arena, e va guardato l'id come in `setArenaEnded`.
   * Qui il server risponde al singolo socket (`arena.handler.ts:627` e `:697`,
   * `socket.emit`, non un broadcast di stanza), ma il socket e' uno solo per
   * l'app: chiesta la pausa in A e passati al dettaglio di B prima che l'ack
   * torni, la conferma di A toglieva dalla coda in B — o, al ritorno, ce la
   * rimetteva senza che nessuno l'avesse chiesto.
   */
  /*
   * La conferma riallinea **entrambe** le verita' del client, non una sola.
   *
   * Qui si scriveva solo `status`, mentre l'ottimistico di `pauseSearch` e
   * `resumeSearch` scrive anche `myParticipant.status`. Bastava un ack fuori
   * ordine -- e arrivano fuori ordine: la ripresa fa piu' query della pausa,
   * quindi puo' concludere per ultima anche se e' partita per prima -- perche'
   * i due campi restassero divisi. Da li' i tre bottoni insieme (Riprendi +
   * Pausa + Esci), perche' la banda leggeva `myParticipant.status` per la
   * pausa e `status` per la ricerca.
   *
   * Lo stato lo detta il server nell'ack: il valore di riserva serve solo ai
   * server piu' vecchi dell'ack con il campo dentro.
   */
  setArenaPaused: (arenaId: string, participantStatus: ArenaParticipantStatus = 'paused', origine?: OriginePausaArena) => {
    const { currentArena, myParticipant, status, originePausa } = get();
    if (!currentArena || currentArena.id !== arenaId) return;
    get().chiudiFasePausa();
    /*
     * Da oggi `arena:paused` puo' arrivare NON RICHIESTO — l'assenza e la fine
     * partita lo mandano da soli — e quindi anche mentre c'e' una partita a
     * schermo. Scrivere `status: 'lobby'` a occhi chiusi, come si faceva quando
     * l'unico mittente era il bottone, avrebbe due effetti gravi: da 'playing' o
     * 'matchFound' l'instradamento di App.tsx porta fuori dalla damiera nel
     * mezzo di una partita, e da 'gameEnd' cancella l'esito che l'utente deve
     * poter leggere al ritorno. In quei tre casi si aggiorna solo l'eco del
     * database, che e' quel che fa comparire «Riprendi».
     */
    const partitaAschermo = status === 'playing' || status === 'matchFound' || status === 'gameEnd';
    /*
     * Senza `origine` non si decide niente: e' il riannuncio di `arena:back`,
     * che dice «sei ancora fermo» e non da dove, e la spiegazione resta quella
     * dell'ultima pausa vera. Quali origini meritino una frase lo decide la
     * banda, non questo punto: qui si registra il fatto.
     */
    set({
      /*
       * L'ack e' la prova che il server ha capito: qualunque cosa dicesse la
       * banda rossa (un `RATE_LIMITED` da doppio tocco, un `NOT_CONNECTED` di
       * poco fa) adesso e' storia vecchia. Senza questa riga il rettangolo
       * restava sotto l'intestazione per tutta la permanenza sulla pagina,
       * perche' a spegnerlo erano solo i caricamenti e l'iscrizione.
       */
      error: null,
      ...(partitaAschermo ? {} : { status: 'lobby' as const }),
      originePausa: origine === undefined ? originePausa : origine,
      ...(myParticipant ? { myParticipant: { ...myParticipant, status: participantStatus } } : {}),
    });
  },

  setArenaResumed: (arenaId: string, participantStatus: ArenaParticipantStatus = 'active') => {
    const { currentArena, myParticipant, status } = get();
    if (!currentArena || currentArena.id !== arenaId) return;
    get().chiudiFasePausa();
    /* Stessa guardia del gemello, per la stessa ragione: una ripresa che
       arrivasse con la damiera a schermo non deve riscrivere il flusso. */
    const partitaAschermo = status === 'playing' || status === 'matchFound' || status === 'gameEnd';
    set({
      /* Stessa ragione del gemello: l'ack chiude il guasto. */
      error: null,
      ...(partitaAschermo ? {} : { status: 'searching' as const }),
      originePausa: null,
      ...(myParticipant ? { myParticipant: { ...myParticipant, status: participantStatus } } : {}),
    });
  },

  setGameStart: (state: BoardState, clock: GameClockInit | null, opening?: DrawnOpeningPayload | null, primaMossa?: PrimaMossaPayload | null) => {
    const currentStatus = get().status;
    log.info('Arena', 'game start event received', { currentStatus, primaMossa });

    /*
     * La scadenza si arma SUBITO, prima di ogni uscita anticipata qui sotto.
     *
     * Sul server il tempo scorre da `startClock()`, mentre qui l'avvio può
     * restare parcheggiato: dietro l'overlay «partita trovata» (tre secondi) o
     * dietro il podio di fine partita (`pendingGameStart`, anche molto di più).
     * Armarla al momento in cui la damiera compare mostrerebbe più tempo di
     * quello vero, e il conto arriverebbe a zero DOPO il forfait.
     */
    set({
      primaMossaScadeAt: primaMossa ? Date.now() + primaMossa.restaMs : null,
      primaMossaEsito: primaMossa?.esito ?? null,
    });

    /*
     * Dalla stessa apertura escono due cose diverse, e vanno ricavate qui una
     * volta sola perché entrambe devono valere anche per la partita messa in
     * coda dietro il podio (`pendingGameStart`).
     *
     * Il CONTO non guarda il modo: sono le semi-mosse che ci sono sulla
     * scacchiera senza che i giocatori abbiano mosso, e se un giorno il server
     * ne applicasse di un altro tipo resterebbero comunque non loro. Il
     * CARTELLINO invece è solo per il sorteggio (`mode: 'drawn'`), che è
     * l'unico che abbia qualcosa da raccontare — sulla stessa riga di
     * `multiplayer-store.ts:628`.
     *
     * Quando non c'è sorteggio il campo `opening` manca del tutto: il server
     * lo omette invece di mandare `mode: 'free'`.
     */
    const openingPlyCount = opening?.moves?.length ?? 0;
    const drawnOpening = annuncioApertura(opening);

    // If game end overlay is showing, queue the game start for later (consumed by onPlayNext → resumeSearch)
    if (currentStatus === 'gameEnd') {
      set({ pendingGameStart: { state, clock, openingPlyCount, drawnOpening } });
      /*
       * Il RITORNO non aspetta un click.
       *
       * La coda qui sopra esiste per non strappare via il podio mentre lo si
       * legge, e per l'accoppiamento normale va benissimo: il giocatore torna
       * in coda quando vuole. Il ritorno no — lato server la stanza e'
       * gia' aperta e l'orologio corre — quindi si consuma subito, e il conto
       * alla rovescia che l'overlay ha mostrato fin qui e' stato l'avviso.
       */
      if (get().ritornoPending) get().consumaPartitaInCoda();
      return;
    }

    // Discard stale game:start if player has paused or is in any non-game state
    if (currentStatus !== 'matchFound' && currentStatus !== 'playing') {
      log.warn('Arena', 'setGameStart discarded in unexpected state', { currentStatus });
      return;
    }

    // Auto-confirm match when game actually starts from server
    set({
      boardState: state,
      openingPlyCount,
      drawnOpening,
      lastMove: null,
      preMoveBoardState: null,
      /* Partita nuova dell'arena: quota di proposte piena. In arena se ne
         giocano molte di fila, quindi senza questa riga il freno diventerebbe
         un tetto per TORNEO invece che per partita. */
      drawOffersLeft: MAX_OFFERTE_PATTA_PER_PARTITA,
      drawMustMove: false,
      ...(currentStatus === 'matchFound' ? { status: 'playing', matchFoundData: null, hasAcknowledgedMatch: true } : {}),
    });
    if (clock) {
      get().initClock(clock);
    }
    /* La chiave, non la fiducia: `resumeSearch` puo' aver gia' suonato per
       questa stessa partita passando da `consumaPartitaInCoda`, e la guardia
       qui sopra lascia passare proprio `playing`. */
    const roomKey = get().currentRoomId;
    playSound('match-found', roomKey ? { onceForKey: roomKey } : undefined);
    const arena = get().currentArena;
    if (arena) trackArenaGameStarted(arena.id, arena.variant);
  },

  setGameUpdate: (state: BoardState, lastMove: Move, clock: ClockSyncEvent | null, roomId?: string) => {
    /*
     * La stanza si guarda anche qui, non solo a monte.
     *
     * `multiplayer-store` instrada per `roomId` e sbaglia solo quando il server
     * quel campo non lo manda (finestra di deploy), dove ricade sull'euristica
     * degli stati. Questa riga e' il fermo di sicurezza di quel ripiego: senza,
     * un aggiornamento di un'ALTRA partita arrivato qui per errore si scriveva
     * su questa damiera senza che niente lo confrontasse con la nostra stanza.
     */
    if (roomId && roomId !== get().currentRoomId) {
      log.warn('Arena', 'setGameUpdate scartato: non e la nostra stanza', { roomId, nostra: get().currentRoomId });
      return;
    }
    const { status } = get();
    if (status !== 'playing') {
      log.warn('Arena', 'setGameUpdate in unexpected state', { status, from: lastMove.from, to: lastMove.to });
    }
    // A non-null snapshot means this echoes our own optimistic move — makeMove
    // already played the sound instantly, so skip it here to avoid a delayed
    // double sound. Opponent moves have no snapshot and still sound on arrival.
    const wasOptimistic = get().preMoveBoardState !== null;
    set({
      boardState: state,
      lastMove,
      preMoveBoardState: null,
      /* Ho mosso io? Allora posso riproporre patta: vedi `utils/sblocco-patta.ts`. */
      ...sbloccoPattaDopoMossa(state.currentTurn, get().playerColor, get().drawMustMove),
    });
    if (clock) {
      get().syncClock(clock);
    }
    if (lastMove && !wasOptimistic) {
      // isPromotion is stripped from lastMove by the server schema; read it from
      // the authoritative broadcast board state (server pushes the resolved move).
      // `isCapture`: una presa suona SEMPRE `move`, mai `promote`. Quando e' una presa
      // multipla questo e' il SALTO 1 — il replay del fantasma (`syncFromExternal`) suona
      // i successivi e la promozione sull'ultimo. Le due chiamate cadono a un fotogramma
      // di distanza sullo stesso elemento audio in cache, quindi si sente un suono solo:
      // e' proprio la regola «presa → move» a renderlo innocuo.
      const ultima = state.moveHistory.at(-1);
      playMoveSound(ultima?.isPromotion ?? false, 'auto', (ultima?.captures.length ?? 0) > 0);
      vibrate(40);
    }
  },

  rollbackOptimisticMove: (message: string) => {
    // Server rejected our optimistic move (rare desync). Restore the pre-move
    // board so it doesn't stay stuck showing a move the server never accepted.
    /*
     * `message` e' la frase inglese di `game:error`, scritta per i log: il
     * codice, l'unica cosa stabile, si ferma in `multiplayer-store.ts` e qui
     * non arriva. Ma di codici che passano di qui ce n'e' uno solo —
     * `MOVE_REJECTED`, l'unico per cui quel file chiama questa funzione — e
     * quello si puo' dire in tutte e sette le lingue. La frase resta nel log.
     */
    log.warn('Arena', 'mossa rifiutata dal server', { message });
    const chiave = 'arena.errors.moveRejected';
    const { preMoveBoardState } = get();
    if (preMoveBoardState) {
      set({ boardState: preMoveBoardState, preMoveBoardState: null, error: chiave });
      return;
    }
    set({ error: chiave });
  },

  setGameEnd: (result: 'white' | 'black' | 'draw', reason: string) => {
    log.info('Arena', 'game end event received', { result, reason });
    const { playerColor, opponent, currentGameId, currentArena, myParticipant, myRank, prevRank, boostActivated, status: currentStatus, pendingMatch, arenaGames } = get();

    // If game:end arrives while showing a previous game's overlay and there's a pending match,
    // it means the opponent resigned the pending game before we consumed it.
    // Clear the pending match so we don't enter a finished game.
    if (currentStatus === 'gameEnd' && pendingMatch) {
      log.warn('Arena', 'game:end arrived for pending match (opponent resigned)', { roomId: pendingMatch.roomId });
      set({ pendingMatch: null, pendingGameStart: null });
      return;
    }

    // State machine validation
    if (currentStatus !== 'playing' && currentStatus !== 'matchFound') {
      log.warn('Arena', 'setGameEnd in unexpected state', { currentStatus });
    }
    // Useful: did standings already arrive?

    // Build lastGameResult for the post-game overlay
    const isWin = result === playerColor;
    const isDraw = result === 'draw';
    const gameResultType = isWin ? 'win' : isDraw ? 'draw' : 'loss';

    /*
     * Il totale non si ricostruisce: si legge da chi l'ha contato.
     *
     * `arena:game-completed` porta i punti calcolati dal server per QUESTA
     * partita (`arena.repository.ts`, `updateStatsAtomic`, che li restituisce
     * dallo stesso UPDATE che li somma), e `setArenaGameCompleted` li deposita
     * in `arenaGames` gia' scelti sul giocatore giusto — `whitePoints` o
     * `blackPoints` secondo l'id dell'utente — insieme al boost registrato dal
     * server.
     *
     * Arriva prima di `game:end`: `game-end.service.ts:310` aspetta
     * `handleArenaGameEnd`, che emette a `arena.handler.ts:1139`, e `game:end`
     * parte solo dopo (`game-end.service.ts:373`); sulla stessa connessione
     * l'ordine e' garantito. E arriva anche a chi e' appena rientrato in
     * partita dopo un ricarico: nella stanza `arena:<id>` ce lo rimette il
     * server con `handleArenaSocketReconnect` (`arena.handler.ts:185`),
     * chiamata su tutte e due le strade del rientro
     * (`connection.handler.ts:374` sul `connect`, `:550` su
     * `game:check-active`), e i gestori del socket sono gia' registrati perche'
     * `loadArena` fa `connect()` prima della `GET` del dettaglio.
     *
     * Questo e' il caso che rendeva il difetto visibile: `rejoinGame` lascia
     * `myParticipant` e `currentArena` nulli finche' quella `GET` non torna — e
     * per sempre, se fallisce — e azzera `boostActivated`. Il client, da solo,
     * non conosceva ne' la serie ne' il boost: una vittoria che sul server
     * valeva 4 veniva annunciata +2. Ora il numero e' quello vero.
     *
     * Il calcolo locale resta come ripiego per quando la voce non c'e' (il
     * `try/catch` che inghiotte in `handleArenaGameEnd`,
     * `arena.handler.ts:1236-1238`; `processGameResult` che torna `null`,
     * `arena.service.ts:307` e `:341`; il `catch` di `game-end.service.ts:324`
     * che lascia partire `game:end` anche se a sollevare e' stato uno dei
     * gestori chiamati prima; uno dei due partecipanti che non si ritrova, che
     * salta l'emit ma non le posizioni (`arena.handler.ts:1138`); una partita
     * che non e' d'arena). Ed e' la formula del server scritta per intero,
     * senza approssimazioni: `WIN * (boostato ? 2 : 1)` per la vittoria, `DRAW`
     * per la patta, `LOSS` per la sconfitta.
     */
    const dalServer = currentGameId
      ? arenaGames.find((g) => g.gameId === currentGameId)
      : undefined;

    /*
     * La formula del server, scritta per intero, una volta sola.
     *
     * Non dipende piu' da niente che il client non abbia in mano: con la serie
     * se n'e' andata l'unica incognita — la serie PRIMA della partita, che solo
     * il database sapeva — e con lei il `>= 3` letterale che la approssimava.
     * Il boost resta, ed e' l'unico fattore: raddoppia la sola vittoria.
     */
    const puntiDi = (boost: boolean) =>
      isWin
        ? ARENA_SCORING.WIN * (boost ? ARENA_SCORING.BOOST_MULTIPLIER : 1)
        : isDraw
          ? ARENA_SCORING.DRAW
          : ARENA_SCORING.LOSS;

    const pointsEarned = dalServer ? dalServer.pointsEarned : puntiDi(boostActivated);
    const boostato = dalServer ? dalServer.boosted : boostActivated;

    /*
     * Il totale del server contro la formula: ora e' un'uguaglianza esatta,
     * senza stato nascosto da indovinare. E' quindi l'unica rete lato web che
     * si accorgerebbe dei letterali dentro l'SQL che divergono da
     * `ARENA_SCORING`. Qui non si sostituisce il numero vero con un ripiego
     * (sarebbe un altro numero falso), si fa rumore.
     *
     * ⚠️ **Dove arriva quel rumore: solo in sviluppo.** `utils/logger.ts` esce
     * subito da `info`, `warn` E `error` quando `import.meta.env.DEV` è falso
     * (`if (!isDev) return;`), e nel logger non c'è nessun aggancio a Sentry —
     * che è inizializzato in `main.tsx` a parte, e solo col consenso analitico.
     * Quindi in produzione questa sentinella **non scrive da nessuna parte**:
     * se scattasse, nessuno lo saprebbe. È una rete che guarda solo chi
     * sviluppa, e va letta così — non come un allarme che qualcuno riceve.
     */
    if (dalServer && !dalServer.pointsVoided && puntiDi(boostato) !== pointsEarned) {
      log.warn('Arena', 'il totale del server non e quello che la formula dice', {
        gameId: currentGameId, pointsEarned, boostato, atteso: puntiDi(boostato),
      });
    }

    // Compute rankDelta: standings update typically arrives before setGameEnd,
    // so myRank is already updated — compare with prevRank saved at match start
    const rankDelta = (prevRank != null && myRank != null) ? (myRank - prevRank) : 0;

    const lastGameResult: import('../types/arena').ArenaGameResult = {
      gameId: currentGameId ?? '',
      arenaId: currentArena?.id ?? '',
      result: gameResultType,
      pointsEarned,
      boostBonus: boostato && isWin,
      /* Come `newRank` qui sotto: se non si conosce, non si dichiara. */
      newPoints: myParticipant?.points ?? null,
      newRank: myRank,
      rankDelta,
      opponentUsername: opponent?.username ?? '',
      /* Se non si conosce, non si dichiara: un 1000 tondo qui è un punteggio inventato. */
      opponentRating: opponent?.rating ?? null,
      opponentAvatar: opponent?.avatar ?? null,
      /*
       * Solo il server lo sa: e' lui a decidere che i punti non si assegnano,
       * e ormai lo fa solo quando la registrazione del punteggio e' fallita e
       * la partita e' stata abbandonata (`PunteggioParziale`) -- una soglia di
       * mosse non c'e' piu'. Senza la sua voce non si dichiara: il ripiego
       * locale non e' in grado di distinguere «zero perche' non c'e' stata
       * partita» da «zero perche' hai perso», ed e' meglio il riquadro normale
       * che una spiegazione inventata.
       */
      pointsVoided: dalServer?.pointsVoided === true,
    };

    set({
      gameResult: result,
      gameEndReason: reason,
      status: 'gameEnd',
      // Clear the optimistic snapshot so a finished game never leaves a dangling
      // preMoveBoardState that could mute a later sound or hijack a game:error.
      preMoveBoardState: null,
      lastGameResult,
      currentGameId: null,
      currentRoomId: null,
      clockState: null,
      drawOffered: false,
      opponentOfferedDraw: false,
      drawDeclinedNotice: false,
      isOpponentDisconnected: false,
      opponentDisconnectedColor: null,
      graceTimeRemaining: null,
    });

    if (currentArena) {
      trackArenaGameCompleted(currentArena.id, currentArena.variant, gameResultType);
    }
  },

  setError: (error: string | null) => {
    set({ error });
  },

  setArenaGameCompleted: (payload: ArenaGameCompletedPayload) => {
    /*
     * `arena:game-completed` e' un broadcast di stanza, e un socket sta in piu'
     * stanze: senza questo filtro la partita di un'arena che si sta solo
     * guardando finiva nella scheda di quella a cui si e' iscritti.
     *
     * Senza arena aperta pero' non c'e' nessuna scheda da sporcare, e c'e'
     * invece una partita che si sta giocando: e' il rientro dopo un ricarico,
     * dove `rejoinGame` mette `status: 'playing'` e lancia `loadArena` senza
     * aspettarla — e se quella GET fallisce, `currentArena` resta nullo per
     * tutta la partita. Li' la propria voce e' l'unica che il totale esatto
     * puo' prendere (`setGameEnd` la cerca per `currentGameId`), e buttarla
     * via riportava a "+2" una vittoria che il server aveva pagato 6. La
     * partita in corso ha un nome: `currentGameId`.
     */
    const { currentArena, currentGameId } = get();
    const miaInCorso = currentGameId != null && payload.game.gameId === currentGameId;
    /*
     * L'eccezione «e' la mia partita» vale SEMPRE, non solo con `currentArena`
     * nullo. La pagina aperta non e' per forza quella in cui si gioca: chi e'
     * in coda in A e apre B dall'elenco resta accoppiabile in A con
     * `currentArena = B`, e li' la propria voce veniva buttata via come quella
     * di un estraneo — con `setGameEnd` a ripiegare sul calcolo locale, cioe'
     * «+2» su una vittoria che il server aveva pagato 6, e i punti mostrati su
     * una partita in realta' annullata (`pointsVoided` perso per strada).
     */
    if (!miaInCorso && currentArena?.id !== payload.arenaId) return;

    const raw = payload.game;
    const userId = useAuthStore.getState().user?.id;
    /*
     * Una partita e' mia se ci ho giocato. Prima `isWhite` cadeva su `false`
     * per le partite altrui, e la riga usciva col bianco messo come
     * "avversario", il colore nero e i punti del nero: dati inventati. Le
     * partite altrui si raccontano dal lato del bianco, ed e' lo stesso conto
     * che il REST fa gia' in `arena.controller.ts`.
     */
    const mia = !!userId && (raw.whiteUserId === userId || raw.blackUserId === userId);
    const isWhite = mia ? raw.whiteUserId === userId : true;
    const game: ArenaGameEntry = {
      gameId: raw.gameId,
      isMine: mia,
      whiteUsername: raw.whiteUsername,
      blackUsername: raw.blackUsername,
      opponentUserId: isWhite ? raw.blackUserId : raw.whiteUserId,
      opponentUsername: isWhite ? raw.blackUsername : raw.whiteUsername,
      opponentRating: isWhite ? raw.blackRating : raw.whiteRating,
      color: isWhite ? 'white' : 'black',
      result: raw.result,
      pointsEarned: isWhite ? raw.whitePoints : raw.blackPoints,
      boosted: isWhite ? raw.whiteBoost : raw.blackBoost,
      /* Vale per tutti e due i giocatori: la partita non c'e' stata. */
      pointsVoided: raw.pointsVoided === true,
      movesCount: raw.movesCount,
      /* `games.id`, non `gameId`: e' l'id con cui si apre la review. Senza
         riportarlo qui, la riga appena aggiunta dal vivo sarebbe l'unica non
         apribile della scheda fino al ricaricamento. */
      savedGameId: raw.savedGameId ?? null,
      createdAt: raw.createdAt,
    };
    get().addCompletedGame(game);

    /*
     * E se il riquadro di fine partita e' gia' composto, si riallinea.
     *
     * `setGameEnd` scatta una fotografia (`lastGameResult`) e per i punti cerca
     * la propria voce in `arenaGames`: oggi l'ordine e' garantito — il server
     * aspetta `handleArenaGameEnd` prima di emettere `game:end` — ma i percorsi
     * in cui `arena:game-completed` NON arriva esistono e sono dichiarati nel
     * codice del server (il `catch` di fine partita, l'emit saltato quando uno
     * dei due partecipanti non si ritrova). In quei casi il ripiego locale
     * calcola `+2` su una partita che il server ha pagato **0**, con
     * `pointsVoided: false`: l'overlay mostra la scomposizione intera mentre la
     * classifica dice zero. E' lo scenario piu' frequente dell'arena piccola.
     *
     * Se il fatto arriva tardi, qui c'e' l'ultima occasione di rimetterlo a
     * posto — e riguarda solo la PROPRIA partita, quella nel riquadro.
     */
    const ultimo = get().lastGameResult;
    if (mia && ultimo && ultimo.gameId === raw.gameId) {
      set({
        lastGameResult: {
          ...ultimo,
          pointsEarned: game.pointsEarned,
          boostBonus: game.boosted && ultimo.result === 'win',
          pointsVoided: game.pointsVoided === true,
        },
      });
    }
  },

  updateArenaPlayerCount: (arenaId: string, playerCount: number) => {
    set((state) => ({
      arenas: state.arenas.map((a) =>
        a.id === arenaId ? { ...a, playerCount } : a
      ),
      currentArena: state.currentArena?.id === arenaId
        ? { ...state.currentArena, playerCount }
        : state.currentArena,
    }));
  },
}));
